import { buildInitialState } from './core/Board'
import { getLegalMoves, isPawnPromotion, getCheckAttackers } from './core/rules'
import { Game } from './core/Game'
import type { BoardEvent, GameConfig, GameState, Move, Pickup, PieceType } from './core/types'
import { getOuterRing, getCenterSquares, getDiagonalStripSquares } from './core/transforms'
import { RoomClient } from './net/RoomClient'
import { initTheme, attachThemeListeners } from './ui/theme'

initTheme()
attachThemeListeners()

// Worker URL: set VITE_WORKER_URL env var for production; falls back to local wrangler dev port
const WORKER_URL: string =
  (import.meta.env as Record<string, string | undefined>)['VITE_WORKER_URL'] ?? 'http://localhost:8787'

import type { Facing } from './core/types'

const FACING_ARROW: Record<Facing, string> = { UP: '▲', DOWN: '▼', LEFT: '◀', RIGHT: '▶' }

// ── Sound Manager ─────────────────────────────────────────────────────────────

class SoundManager {
  private ctx: AudioContext | null = null

  private getCtx(): AudioContext {
    if (!this.ctx) this.ctx = new AudioContext()
    return this.ctx
  }

  private tone(freq: number, duration: number, type: OscillatorType, gain: number, decay?: number) {
    const ctx = this.getCtx()
    const osc = ctx.createOscillator()
    const gainNode = ctx.createGain()
    osc.connect(gainNode)
    gainNode.connect(ctx.destination)
    osc.type = type
    osc.frequency.setValueAtTime(freq, ctx.currentTime)
    gainNode.gain.setValueAtTime(gain, ctx.currentTime)
    gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + (decay ?? duration))
    osc.start(ctx.currentTime)
    osc.stop(ctx.currentTime + duration)
  }

  move() {
    this.tone(440, 0.06, 'triangle', 0.18)
  }

  capture() {
    // Low thud + high accent
    this.tone(180, 0.12, 'sawtooth', 0.22, 0.10)
    setTimeout(() => this.tone(660, 0.07, 'triangle', 0.14), 40)
  }

  check() {
    this.getCtx()  // ensure context is created
    // Three ascending stabs
    this.tone(523, 0.09, 'square', 0.12)
    setTimeout(() => this.tone(659, 0.09, 'square', 0.12), 100)
    setTimeout(() => this.tone(784, 0.14, 'square', 0.16), 200)
  }

  yourTurn() {
    // Bright ascending fanfare: C5→E5→G5→C6, sine, rings out
    this.tone(523, 0.18, 'sine', 0.22, 0.35)    // C5
    setTimeout(() => this.tone(659, 0.18, 'sine', 0.24, 0.38), 110) // E5
    setTimeout(() => this.tone(784, 0.18, 'sine', 0.26, 0.42), 220) // G5
    setTimeout(() => this.tone(1047, 0.28, 'sine', 0.28, 0.55), 340) // C6 — resolves high
  }

  eventFire() {
    // Rising sweep
    const ctx = this.getCtx()
    const osc = ctx.createOscillator()
    const gainNode = ctx.createGain()
    osc.connect(gainNode)
    gainNode.connect(ctx.destination)
    osc.type = 'sine'
    osc.frequency.setValueAtTime(200, ctx.currentTime)
    osc.frequency.exponentialRampToValueAtTime(800, ctx.currentTime + 0.35)
    gainNode.gain.setValueAtTime(0.18, ctx.currentTime)
    gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4)
    osc.start(ctx.currentTime)
    osc.stop(ctx.currentTime + 0.4)
  }
}

const sound = new SoundManager()

// ── Renderer ──────────────────────────────────────────────────────────────────

class Renderer {
  private boardElement: HTMLElement
  private selectedKey: string | null = null
  private legalMoves: string[] = []
  private cursorKey: string | null = null
  private hoverPreviewMoves: string[] = []
  private preSelectedKey: string | null = null
  private rotationDeg: number = 0
  private eventHighlights: Map<string, string> = new Map()  // squareKey → CSS class
  private onSquareClick: (key: string) => void = () => {}
  private onSquareHover: (key: string | null) => void = () => {}

  constructor(boardElement: HTMLElement) {
    this.boardElement = boardElement
  }

  setClickHandler(handler: (key: string) => void) {
    this.onSquareClick = handler
  }

  setHoverHandler(handler: (key: string | null) => void) {
    this.onSquareHover = handler
  }

  setPreSelection(key: string | null) {
    this.preSelectedKey = key
  }

  setRotation(deg: number) {
    this.rotationDeg = deg
    this.boardElement.style.transform = deg !== 0 ? `rotate(${deg}deg)` : ''
  }

  getRotation(): number { return this.rotationDeg }

  setSelection(selectedKey: string | null, legalMoves: string[]) {
    this.selectedKey = selectedKey
    this.legalMoves = legalMoves
  }

  setCursor(key: string | null) {
    this.cursorKey = key
  }

  setEventHighlights(highlights: Map<string, string>) {
    this.eventHighlights = highlights
  }

  /** Efficiently add/remove hover-preview classes without re-rendering the board. */
  updateHoverPreview(moves: string[], state: GameState) {
    // Remove old preview classes
    for (const key of this.hoverPreviewMoves) {
      const el = this.boardElement.querySelector(`[data-key="${key}"]`)
      el?.classList.remove('square-hover-legal', 'square-hover-capture-preview')
    }
    this.hoverPreviewMoves = moves
    if (this.selectedKey !== null) return  // suppress preview while a piece is selected
    for (const key of moves) {
      const el = this.boardElement.querySelector(`[data-key="${key}"]`)
      if (!el) continue
      el.classList.add(state.squares[key]?.piece ? 'square-hover-capture-preview' : 'square-hover-legal')
    }
  }

  render(state: GameState) {
    this.hoverPreviewMoves = []  // DOM is rebuilt; hover must reapply if needed
    this.boardElement.innerHTML = ''
    this.boardElement.className = `board board-${state.boardSize}`

    // Build set of squares holding a king that is in check
    const checkKingSquares = new Set<string>()
    for (const [key, sq] of Object.entries(state.squares)) {
      if (sq.piece?.type === 'king') {
        const owner = state.players[sq.piece.owner]
        if (owner?.alive && owner.inCheck) checkKingSquares.add(key)
      }
    }

    // Build set of squares containing pieces that are delivering check
    const checkAttackerSquares = new Set<string>()
    for (const player of state.players) {
      if (player.alive && player.inCheck) {
        for (const key of getCheckAttackers(state, player.index)) {
          checkAttackerSquares.add(key)
        }
      }
    }

    for (let row = 0; row < state.boardSize; row++) {
      for (let col = 0; col < state.boardSize; col++) {
        const key = `${col},${row}`
        const square = state.squares[key]
        if (!square) continue

        const el = document.createElement('div')
        el.className = 'square'
        el.style.gridColumn = `${col + 1}`
        el.style.gridRow = `${row + 1}`
        el.dataset.key = key
        el.setAttribute('tabindex', '0')
        el.setAttribute('role', 'button')
        el.setAttribute('aria-label', key)

        // Checkerboard coloring
        const isLight = (col + row) % 2 === 0
        el.classList.add(isLight ? 'square-light' : 'square-dark')

        // Upcoming board event highlight — color-coded by event type
        const highlightClass = this.eventHighlights.get(key)
        if (highlightClass) el.classList.add(highlightClass)

        // King-in-check highlight (applied before selection so selection can override)
        if (checkKingSquares.has(key)) el.classList.add('square-king-check')

        // Attacker highlight — green outline on piece(s) delivering check
        if (checkAttackerSquares.has(key)) el.classList.add('square-check-attacker')

        // Keyboard cursor
        if (key === this.cursorKey) el.classList.add('square-cursor')

        // Selection highlight
        if (key === this.selectedKey) el.classList.add('square-selected')
        if (this.legalMoves.includes(key)) {
          el.classList.add(square.piece ? 'square-capture' : 'square-legal')
        }
        // Pre-selection highlight
        if (key === this.preSelectedKey) el.classList.add('square-preselected')

        // Piece — wrapped in a counter-rotating container so pieces appear upright when board is rotated
        if (square.piece) {
          const pieceContainer = document.createElement('div')
          pieceContainer.style.cssText = `position:absolute;inset:0;display:flex;align-items:center;justify-content:center${this.rotationDeg !== 0 ? `;transform:rotate(${-this.rotationDeg}deg)` : ''}`

          const pieceEl = document.createElement('div')
          pieceEl.className = 'piece'
          pieceEl.textContent = getPieceSymbol(square.piece.type, square.piece.owner)
          pieceEl.style.color = state.players[square.piece.owner]?.color ?? '#fff'
          pieceContainer.appendChild(pieceEl)

          el.appendChild(pieceContainer)

          // Pawn direction indicator — placed outside the counter-rotating container so it
          // rotates naturally with the board and always points in the visual advance direction.
          if (square.piece.type === 'pawn') {
            const arrowEl = document.createElement('div')
            arrowEl.className = `pawn-arrow pawn-arrow-${square.piece.facing.toLowerCase()}`
            arrowEl.textContent = FACING_ARROW[square.piece.facing]
            arrowEl.style.color = state.players[square.piece.owner]?.color ?? '#fff'
            el.appendChild(arrowEl)
          }
        }

        // Pickup icon — top-left corner, counter-rotated to stay upright
        const pickup = state.pickups[key]
        if (pickup) {
          const pickupEl = document.createElement('div')
          pickupEl.className = 'pickup-icon'
          pickupEl.textContent = pickupIcon(pickup)
          pickupEl.title = pickupLabel(pickup)
          pickupEl.style.color = pickupColor(pickup)
          if (this.rotationDeg !== 0) {
            pickupEl.style.transform = `rotate(${-this.rotationDeg}deg)`
          }
          el.appendChild(pickupEl)
        }

        el.addEventListener('click', () => this.onSquareClick(key))
        el.addEventListener('mouseenter', () => this.onSquareHover(key))
        el.addEventListener('mouseleave', () => this.onSquareHover(null))
        this.boardElement.appendChild(el)
      }
    }

    this.boardElement.style.boxShadow = buildEdgeGlow(state)
  }
}

// ── Board overlay announcements ───────────────────────────────────────────────
// Shared mechanic for communicating notable board events to all players.
// Shows a full-board semi-transparent overlay with an icon + name, then fades out.
function showBoardOverlay(icon: string, name: string, color: string) {
  const boardEl = document.getElementById('board')
  if (!boardEl) return
  const rect = boardEl.getBoundingClientRect()
  const overlay = document.createElement('div')
  overlay.className = 'board-event-overlay'
  overlay.style.cssText += `left:${rect.left}px;top:${rect.top}px;width:${rect.width}px;height:${rect.height}px;color:${color}`
  const iconEl = document.createElement('div')
  iconEl.className = 'board-event-overlay-icon'
  iconEl.textContent = icon
  const nameEl = document.createElement('div')
  nameEl.className = 'board-event-overlay-name'
  nameEl.textContent = name
  overlay.appendChild(iconEl)
  overlay.appendChild(nameEl)
  document.body.appendChild(overlay)
  overlay.addEventListener('animationend', () => overlay.remove())
}

// ── Piece slide animation ──────────────────────────────────────────────────────
// Uses a fixed-position ghost element that slides from source to destination,
// while the real piece at the destination is briefly hidden until the ghost lands.
// Easing: cubic-bezier(0.76,0,0.24,1) — slow pickup, fast flight, soft landing.
function animatePieceMove(
  boardEl: HTMLElement,
  fromRect: DOMRect,
  toKey: string,
  symbol: string,
  color: string,
  rotationDeg: number
) {
  const toEl = boardEl.querySelector<HTMLElement>(`[data-key="${toKey}"]`)
  if (!toEl) return

  const toRect = toEl.getBoundingClientRect()
  const dx = toRect.left - fromRect.left
  const dy = toRect.top - fromRect.top
  if (dx === 0 && dy === 0) return

  // Hide the real piece at destination until the ghost lands
  const destPieceContainer = toEl.querySelector<HTMLElement>('div.piece')?.parentElement as HTMLElement | null
  if (destPieceContainer) destPieceContainer.style.opacity = '0'

  // Ghost element — fixed at source square position
  const ghost = document.createElement('div')
  ghost.style.cssText = `position:fixed;left:${fromRect.left}px;top:${fromRect.top}px;width:${fromRect.width}px;height:${fromRect.height}px;display:flex;align-items:center;justify-content:center;pointer-events:none;z-index:999`

  const ghostPiece = document.createElement('div')
  ghostPiece.className = 'piece'
  ghostPiece.textContent = symbol
  ghostPiece.style.color = color
  if (rotationDeg !== 0) ghostPiece.style.transform = `rotate(${-rotationDeg}deg)`
  ghost.appendChild(ghostPiece)
  document.body.appendChild(ghost)

  void ghost.offsetWidth  // force reflow so transition fires

  const dur = 210
  ghost.style.transition = `transform ${dur}ms cubic-bezier(0.76,0,0.24,1)`
  ghost.style.transform = `translate(${dx}px,${dy}px)`

  setTimeout(() => {
    ghost.remove()
    if (destPieceContainer) destPieceContainer.style.opacity = ''
  }, dur + 16)
}

/**
 * Format an internal "col,row" key using board notation.
 * 8×8: columns H→A (col 0=H … col 7=A), rows 1→8 (row 0=1 … row 7=8).
 * 14×14: fall back to raw col,row.
 */
// ── Event banner + highlights ──────────────────────────────────────────────────

const EVENT_LABELS: Record<string, string> = {
  outer_ring_cw:  'Ring 90° CW',
  outer_ring_ccw: 'Ring 90° CCW',
  center_180:     'Center 180°',
  diagonal_ne:    'Diagonal Shift ↗',
  diagonal_sw:    'Diagonal Shift ↙',
  diagonal_nw:    'Diagonal Shift ↖',
  diagonal_se:    'Diagonal Shift ↘',
}

const EVENT_ICONS: Record<string, string> = {
  outer_ring_cw:  '↻',
  outer_ring_ccw: '↺',
  center_180:     '↕',
  diagonal_ne:    '↗',
  diagonal_sw:    '↙',
  diagonal_nw:    '↖',
  diagonal_se:    '↘',
}

/** Diagonal icons adjusted for board visual rotation (0/90/180/270 degrees CW). */
const DIAG_ICON_BY_ROTATION: Record<string, Record<number, string>> = {
  diagonal_ne: { 0: '↗', 90: '↘', 180: '↙', 270: '↖' },
  diagonal_sw: { 0: '↙', 90: '↖', 180: '↗', 270: '↘' },
  diagonal_nw: { 0: '↖', 90: '↗', 180: '↘', 270: '↙' },
  diagonal_se: { 0: '↘', 90: '↙', 180: '↖', 270: '↗' },
}

function orientedEventIcon(eventType: string, boardRotation: number): string {
  return DIAG_ICON_BY_ROTATION[eventType]?.[boardRotation] ?? EVENT_ICONS[eventType] ?? '⚡'
}

function orientedEventLabel(eventType: string, boardRotation: number): string {
  if (eventType.startsWith('diagonal_')) {
    return `Diagonal Shift ${orientedEventIcon(eventType, boardRotation)}`
  }
  return EVENT_LABELS[eventType] ?? eventType
}

/** CSS class applied to squares in the affected zone, keyed by event type. */
const EVENT_HIGHLIGHT_CLASS: Record<string, string> = {
  outer_ring_cw:   'square-event-cw',
  outer_ring_ccw:  'square-event-ccw',
  center_180:      'square-event-180',
  diagonal_ne:     'square-event-diag-ne',
  diagonal_sw:     'square-event-diag-sw',
  diagonal_nw:     'square-event-diag-nw',
  diagonal_se:     'square-event-diag-se',
}

/** Accent color used in the banner text, keyed by event type. */
const EVENT_COLOR: Record<string, string> = {
  outer_ring_cw:  '#ffaa00',   // amber
  outer_ring_ccw: '#00c8dc',   // cyan
  center_180:     '#b464ff',   // violet
  diagonal_ne:    '#4ade80',   // green
  diagonal_sw:    '#4ade80',   // green
  diagonal_nw:    '#f472b6',   // pink
  diagonal_se:    '#f472b6',   // pink
}

// ── Pickup rendering helpers ───────────────────────────────────────────────────

const PICKUP_PIECE_ICONS: Record<string, string> = {
  queen: '♛', rook: '♜', bishop: '♝', knight: '♞', pawn: '♟',
}

function pickupIcon(pickup: Pickup): string {
  if (pickup.kind === 'upgrade') return '⬆'
  return PICKUP_PIECE_ICONS[pickup.to] ?? '?'
}

function pickupLabel(pickup: Pickup): string {
  if (pickup.kind === 'upgrade') return 'Upgrade'
  return `Become ${pickup.to}`
}

function pickupColor(pickup: Pickup): string {
  if (pickup.kind === 'upgrade') return '#fbbf24'   // amber-gold
  return { queen: '#c084fc', rook: '#60a5fa', bishop: '#34d399', knight: '#f87171', pawn: '#94a3b8' }[pickup.to] ?? '#fff'
}

function getEventAffectedSquares(event: BoardEvent, boardSize: 8 | 14, validKeys?: Set<string>): string[] {
  if (event.type === 'center_180') return getCenterSquares(boardSize)
  if (event.type === 'diagonal_ne' || event.type === 'diagonal_sw') {
    const keys = validKeys ?? new Set(Array.from({ length: boardSize }, (_, col) =>
      Array.from({ length: boardSize }, (_, row) => `${col},${row}`)
    ).flat())
    return getDiagonalStripSquares(boardSize, 'NESW', keys)
  }
  if (event.type === 'diagonal_nw' || event.type === 'diagonal_se') {
    const keys = validKeys ?? new Set(Array.from({ length: boardSize }, (_, col) =>
      Array.from({ length: boardSize }, (_, row) => `${col},${row}`)
    ).flat())
    return getDiagonalStripSquares(boardSize, 'NWSE', keys)
  }
  return getOuterRing(boardSize)
}

function setEventZoneOverlays(events: BoardEvent[], state: GameState) {
  const boardEl = document.getElementById('board')
  if (!boardEl) return

  boardEl.querySelectorAll('.event-zone-overlay').forEach(el => el.remove())

  events.forEach((event, idx) => {
    const isNext = idx === 0
    const color = EVENT_COLOR[event.type] ?? '#ffa500'
    const overlay = document.createElement('div')
    overlay.className = 'event-zone-overlay'

    if (event.type === 'center_180') {
      const [c0, r0] = state.boardSize === 8 ? [2, 2] : [5, 5]
      const [c1, r1] = state.boardSize === 8 ? [5, 5] : [8, 8]
      const tlEl = boardEl.querySelector<HTMLElement>(`[data-key="${c0},${r0}"]`)
      const brEl = boardEl.querySelector<HTMLElement>(`[data-key="${c1},${r1}"]`)
      if (!tlEl || !brEl) return
      const boardRect = boardEl.getBoundingClientRect()
      const tlRect = tlEl.getBoundingClientRect()
      const brRect = brEl.getBoundingClientRect()
      overlay.style.left   = `${tlRect.left - boardRect.left}px`
      overlay.style.top    = `${tlRect.top  - boardRect.top}px`
      overlay.style.width  = `${brRect.right  - tlRect.left}px`
      overlay.style.height = `${brRect.bottom - tlRect.top}px`
    } else {
      // Outer ring — cover the full board
      overlay.style.left   = '0'
      overlay.style.top    = '0'
      overlay.style.width  = '100%'
      overlay.style.height = '100%'
    }

    overlay.style.border       = isNext ? `3px solid ${color}` : `2px dashed ${color}`
    overlay.style.borderRadius = '3px'
    overlay.style.opacity      = isNext ? '0.8' : '0.35'
    boardEl.appendChild(overlay)
  })
}

function updateEventBanner(state: GameState, renderer: Renderer, boardRotation = 0) {
  const bannerEl = document.getElementById('event-banner')
  if (!bannerEl) return

  const sorted = [...state.eventQueue].sort((a, b) => a.firesOnTurn - b.firesOnTurn)

  if (sorted.length === 0) {
    bannerEl.style.display = 'none'
    renderer.setEventHighlights(new Map())
    setEventZoneOverlays([], state)
    return
  }

  // Show only the next upcoming event in the forecast card
  const next = sorted[0]
  const label = orientedEventLabel(next.type, boardRotation)
  const icon = orientedEventIcon(next.type, boardRotation)
  const turns = next.firesOnTurn - state.turnNumber
  const color = EVENT_COLOR[next.type] ?? '#ffa500'
  const countdownText = turns === 0 ? '⚠ THIS TURN' : turns === 1 ? 'next turn' : `in ${turns} turns`

  bannerEl.innerHTML = `<div class="event-banner-header">⚡ Chaos Forecast</div>
    <div class="event-row" style="border-left-color:${color}">
      <span class="event-row-icon">${icon}</span>
      <span class="event-row-label" style="color:${color}">${label}</span>
      <span class="event-row-countdown">${countdownText}</span>
    </div>`
  bannerEl.style.display = 'flex'

  // Icon-only highlight: add CSS class to next event's squares (no tint, no overlay)
  const highlights = new Map<string, string>()
  const nextCls = EVENT_HIGHLIGHT_CLASS[next.type] ?? 'square-event-cw'
  const validKeys = new Set(Object.keys(state.squares))
  for (const sq of getEventAffectedSquares(next, state.boardSize, validKeys)) {
    highlights.set(sq, nextCls)
  }
  renderer.setEventHighlights(highlights)
  // Clear any leftover overlays
  document.getElementById('board')?.querySelectorAll('.event-zone-overlay').forEach(el => el.remove())
}

function hexToRgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

function facingEdgeGlow(facing: Facing, color: string): string {
  // Glow appears OUTSIDE the board on the edge where the player SITS.
  // UP = sits at bottom, DOWN = sits at top, RIGHT = sits at left, LEFT = sits at right.
  // Using outer (non-inset) shadows so they aren't hidden behind opaque board squares.
  const c = hexToRgba(color, 0.8)
  switch (facing) {
    case 'UP':    return `0 18px 24px -4px ${c}`    // bottom outside
    case 'DOWN':  return `0 -18px 24px -4px ${c}`   // top outside
    case 'RIGHT': return `-18px 0 24px -4px ${c}`   // left outside
    case 'LEFT':  return `18px 0 24px -4px ${c}`    // right outside
  }
}

function buildEdgeGlow(state: GameState): string {
  const base = '0 8px 32px rgba(0, 0, 0, 0.4)'
  if (state.phase === 'gameover') return base

  const toHighlight: number[] = []
  if (state.mode === 'sequential') {
    toHighlight.push(state.activePlayer)
  } else if (state.mode === 'simultaneous_resolution') {
    const pendingSet = new Set(state.pendingMoves.map(m => m.player))
    for (const p of state.players) {
      if (p.alive && !pendingSet.has(p.index)) toHighlight.push(p.index)
    }
  } else {
    // simultaneous_instantaneous: highlight players who haven't submitted this window
    const pendingSet = new Set(state.pendingMoves.map(m => m.player))
    for (const p of state.players) {
      if (p.alive && (state.windowOpenAt === null || !pendingSet.has(p.index))) toHighlight.push(p.index)
    }
  }

  const glows = toHighlight
    .map(i => state.players[i])
    .filter(p => p?.alive)
    .map(p => facingEdgeGlow(p.facing, p.color))

  return [base, ...glows].join(', ')
}

function formatCoord(key: string, boardSize: 8 | 14): string {
  const [col, row] = key.split(',').map(Number)
  if (boardSize === 8) {
    const file = String.fromCharCode('H'.charCodeAt(0) - col)  // col 0→H, col 7→A
    const rank = row + 1                                         // row 0→1, row 7→8
    return `${file}${rank}`
  }
  return `${col},${row}`
}

function getPieceSymbol(type: string, owner: number): string {
  const symbols: Record<string, string[]> = {
    king:   ['♔', '♚', '♔', '♔'],
    queen:  ['♕', '♛', '♕', '♕'],
    rook:   ['♖', '♜', '♖', '♖'],
    bishop: ['♗', '♝', '♗', '♗'],
    knight: ['♘', '♞', '♘', '♘'],
    pawn:   ['♙', '♟', '♙', '♙'],
  }
  return symbols[type]?.[owner % 2] ?? '?'
}

// ── Recent Games (localStorage) ───────────────────────────────────────────────

type RecentGame = {
  roomCode: string | null
  playerIndex: number
  playerName: string
  mode: string
  playerCount: number
  timestamp: number
  completed?: boolean
}

function saveRecentGame(entry: RecentGame) {
  const stored = JSON.parse(localStorage.getItem('chaos-chess-recent') ?? '[]') as RecentGame[]
  // Deduplicate by roomCode for online games — keep the newest entry for each room
  const filtered = entry.roomCode
    ? stored.filter(e => e.roomCode !== entry.roomCode)
    : stored
  const updated = [entry, ...filtered].slice(0, 5)
  localStorage.setItem('chaos-chess-recent', JSON.stringify(updated))
}

function loadRecentGames(): RecentGame[] {
  try {
    return JSON.parse(localStorage.getItem('chaos-chess-recent') ?? '[]') as RecentGame[]
  } catch { return [] }
}

function removeRecentGame(roomCode: string) {
  const stored = loadRecentGames().filter(e => e.roomCode !== roomCode)
  localStorage.setItem('chaos-chess-recent', JSON.stringify(stored))
}

function markRecentGameCompleted(roomCode: string) {
  const stored = loadRecentGames().map(e =>
    e.roomCode === roomCode ? { ...e, completed: true } : e
  )
  localStorage.setItem('chaos-chess-recent', JSON.stringify(stored))
}

// ── Setup Screen ──────────────────────────────────────────────────────────────

function showSetupScreen() {
  const setupElement = document.getElementById('setup-screen')
  const gameContainer = document.getElementById('game-container')
  if (!setupElement || !gameContainer) return

  setupElement.innerHTML = `
    <h1>Chaos Chess</h1>
    <div class="setup-form">
      <label>
        Players:
        <select id="player-count">
          <option value="2">2 Players (8×8 board)</option>
          <option value="3">3 Players (14×14 board)</option>
          <option value="4">4 Players (14×14 board)</option>
        </select>
      </label>
      <label>
        Loss Condition:
        <select id="loss-condition">
          <option value="king_captured">King captured (standard)</option>
          <option value="all_pieces">All pieces captured</option>
        </select>
      </label>
      <label id="elimination-mode-label" style="display:none">
        Elimination Mode:
        <select id="elimination-mode">
          <option value="last_standing">Last Standing (all must be eliminated)</option>
          <option value="first_blood">First Blood (game ends on first elimination)</option>
        </select>
      </label>
      <label>
        Game Mode:
        <select id="game-mode">
          <option value="sequential">Sequential</option>
          <option value="simultaneous_resolution">Simultaneous (Resolution)</option>
          <option value="simultaneous_instantaneous">Simultaneous (Instantaneous)</option>
        </select>
      </label>
      <label>
        Chaos frequency:
        <select id="event-cadence">
          <option value="3">Very frequent (every 3 turns)</option>
          <option value="5" selected>Frequent (every 5 turns)</option>
          <option value="7">Normal (every 7 turns)</option>
          <option value="10">Rare (every 10 turns)</option>
        </select>
      </label>
      <label>
        Power-up placement:
        <select id="pickup-placement">
          <option value="balanced" selected>Balanced (one per player zone)</option>
          <option value="random">Random</option>
        </select>
      </label>
      <label>
        Timer (seconds per turn):
        <select id="timer-duration">
          <option value="0">Unlimited</option>
          <option value="15">15s</option>
          <option value="30" selected>30s</option>
          <option value="60">60s</option>
        </select>
      </label>
      <div id="bot-slots-section" style="margin-top:10px">
        <label style="display:block;margin-bottom:6px">Player types:</label>
        <div id="bot-slots-container" style="display:flex;gap:8px;flex-wrap:wrap"></div>
      </div>
      <button id="start-game">Start Game</button>
      <div style="margin-top:10px;text-align:center">
        <a href="/chaos-events.html" target="_blank" style="font-size:0.8rem;color:#888;text-decoration:none;opacity:0.7">⚡ Chaos field guide</a>
      </div>
    </div>

    <hr style="border:none;border-top:1px solid #333;margin:20px 0">
    <h3 style="margin:0 0 12px;color:#aaa;font-size:1rem">Play Online</h3>
    <div style="display:flex;gap:8px;margin-bottom:12px">
      <button id="tab-create" class="online-tab active-tab">Create Room</button>
      <button id="tab-join" class="online-tab">Join Room</button>
    </div>
    <div id="create-panel">
      <label>Your name:
        <input id="creator-name" type="text" maxlength="20" placeholder="Player 1" autocomplete="off"
          style="margin-left:8px;padding:4px 8px;background:#222;border:1px solid #555;color:#eee;border-radius:4px">
      </label>
      <button id="create-room-btn" style="margin-top:10px">Create Room</button>
    </div>
    <div id="join-panel" style="display:none">
      <label>Room code:
        <input id="join-code" type="text" maxlength="6" placeholder="XKCD42" autocomplete="off"
          style="margin-left:8px;padding:4px 8px;background:#222;border:1px solid #555;color:#eee;border-radius:4px;text-transform:uppercase;width:10ch">
      </label>
      <label style="display:block;margin-top:8px">Your name:
        <input id="join-name" type="text" maxlength="20" placeholder="Guest" autocomplete="off"
          style="margin-left:8px;padding:4px 8px;background:#222;border:1px solid #555;color:#eee;border-radius:4px">
      </label>
      <button id="join-room-btn" style="margin-top:10px">Join Room</button>
    </div>
    <p id="online-status" style="min-height:1.4em;color:#888;margin:10px 0 0;font-size:0.9rem"></p>

    <a href="/docs.html" class="docs-link">📖 Docs</a>
    <div id="recent-games-section"></div>
  `

  // Show/hide elimination mode selector based on player count
  const playerCountSel = document.getElementById('player-count') as HTMLSelectElement
  const elimLabel = document.getElementById('elimination-mode-label') as HTMLElement
  function updateElimVisibility() {
    elimLabel.style.display = parseInt(playerCountSel.value) >= 3 ? '' : 'none'
  }
  playerCountSel.addEventListener('change', updateElimVisibility)
  updateElimVisibility()

  // Bot slot toggles — dynamically rendered per-slot Human/Bot buttons
  const playerColors = ['#ffffff', '#ff4444', '#4444ff', '#44ff44']
  const playerNames = ['White', 'Red', 'Blue', 'Green']
  const botSlotState: boolean[] = [false, false, false, false]  // false = human, true = bot

  function renderBotSlots() {
    const count = parseInt(playerCountSel.value)
    const container = document.getElementById('bot-slots-container')
    if (!container) return
    container.innerHTML = ''
    for (let i = 0; i < count; i++) {
      const isBot = botSlotState[i] ?? false
      const btn = document.createElement('button')
      btn.id = `bot-slot-${i}`
      btn.textContent = isBot ? `🤖 ${playerNames[i]}` : `👤 ${playerNames[i]}`
      btn.style.cssText = [
        `background:${isBot ? '#2a2a4a' : '#1e3a1e'}`,
        `color:${playerColors[i]}`,
        'border:1px solid #555',
        'border-radius:6px',
        'padding:5px 12px',
        'cursor:pointer',
        'font-size:0.88rem',
        'font-weight:bold',
      ].join(';')
      btn.onclick = () => {
        botSlotState[i] = !botSlotState[i]
        renderBotSlots()
      }
      container.appendChild(btn)
    }
  }

  playerCountSel.addEventListener('change', renderBotSlots)
  renderBotSlots()

  function readBotSlots(): boolean[] {
    const count = parseInt(playerCountSel.value)
    return Array.from({ length: count }, (_, i) => botSlotState[i] ?? false)
  }

  document.getElementById('start-game')!.onclick = () => {
    const playerCount = parseInt(
      (document.getElementById('player-count') as HTMLSelectElement).value
    ) as 2 | 3 | 4
    const boardSize = playerCount === 2 ? 8 : 14 as 8 | 14

    const rawElimMode = (document.getElementById('elimination-mode') as HTMLSelectElement | null)?.value
    const eliminationMode = (playerCount >= 3 && rawElimMode === 'first_blood')
      ? 'first_blood' as const
      : 'last_standing' as const

    const rawLossCondition = (document.getElementById('loss-condition') as HTMLSelectElement).value
    const lossCondition = rawLossCondition === 'all_pieces' ? 'all_pieces' as const : 'king_captured' as const

    const config: GameConfig = {
      boardSize,
      playerCount,
      mode: (document.getElementById('game-mode') as HTMLSelectElement).value as GameConfig['mode'],
      eliminationMode,
      lossCondition,
      timerDuration: parseInt((document.getElementById('timer-duration') as HTMLSelectElement).value),
      eventsEnabled: true,
      eventCadence: parseInt((document.getElementById('event-cadence') as HTMLSelectElement).value),
      startLayout: 'side',
      pickupPlacement: (document.getElementById('pickup-placement') as HTMLSelectElement).value as 'random' | 'balanced',
      botSlots: readBotSlots(),
    }

    startGame(config)
  }

  // ── Online mode tab toggle ────────────────────────────────────────────────
  const tabCreate = document.getElementById('tab-create')!
  const tabJoin   = document.getElementById('tab-join')!
  const createPanel = document.getElementById('create-panel')!
  const joinPanel   = document.getElementById('join-panel')!

  tabCreate.onclick = () => {
    tabCreate.classList.add('active-tab'); tabJoin.classList.remove('active-tab')
    createPanel.style.display = ''; joinPanel.style.display = 'none'
  }
  tabJoin.onclick = () => {
    tabJoin.classList.add('active-tab'); tabCreate.classList.remove('active-tab')
    joinPanel.style.display = ''; createPanel.style.display = 'none'
  }

  // ── Recent Games ─────────────────────────────────────────────────────────
  function renderRecentGames() {
    const container = document.getElementById('recent-games-section')
    if (!container) return
    const recent = loadRecentGames()
    if (recent.length === 0) { container.innerHTML = ''; return }

    const MODE_SHORT: Record<string, string> = {
      sequential: 'Sequential',
      simultaneous_resolution: 'Simultaneous',
      simultaneous_instantaneous: 'Simultaneous (instant)',
    }

    const rows = recent.map(g => {
      const date = new Date(g.timestamp).toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
      const modeLabel = MODE_SHORT[g.mode] ?? g.mode
      const isOnline = g.roomCode !== null
      const rejoinBtn = !isOnline
        ? `<span style="color:#555;font-size:0.8rem">Local game</span>`
        : g.completed
          ? `<span style="color:#555;font-size:0.8rem">Ended</span>`
          : `<button class="recent-rejoin-btn" data-code="${g.roomCode}" data-name="${g.playerName}" style="padding:3px 12px;font-size:0.8rem;cursor:pointer;background:#1e3a1e;border:1px solid #4caf50;border-radius:4px;color:#ccc">Rejoin</button>`
      return `
        <div class="recent-game-row" style="display:flex;align-items:center;justify-content:space-between;padding:6px 0;border-bottom:1px solid #2a2a2a;gap:8px">
          <div style="flex:1;min-width:0">
            <span style="color:#ccc;font-weight:bold">${isOnline ? g.roomCode : 'Local'}</span>
            <span style="color:#666;font-size:0.8rem;margin-left:6px">${g.playerCount}P · ${modeLabel}</span>
            <div style="color:#666;font-size:0.78rem">${g.playerName} · ${date}</div>
          </div>
          ${rejoinBtn}
        </div>`
    }).join('')

    container.innerHTML = `
      <hr style="border:none;border-top:1px solid #333;margin:20px 0">
      <h3 style="margin:0 0 10px;color:#aaa;font-size:1rem">Recent Games</h3>
      ${rows}
    `

    // Wire rejoin buttons
    for (const btn of container.querySelectorAll<HTMLButtonElement>('.recent-rejoin-btn')) {
      btn.onclick = async () => {
        const code = btn.dataset.code!
        const name = btn.dataset.name ?? 'Guest'
        const storedSession = JSON.parse(sessionStorage.getItem('chaos-chess-session') ?? 'null') as { roomCode: string; playerIndex: number } | null
        const savedIndex = storedSession?.roomCode === code ? storedSession.playerIndex : undefined

        const status = document.getElementById('online-status')
        if (status) status.textContent = `Rejoining ${code}…`

        // Switch to join tab and pre-fill
        tabJoin.click()
        ;(document.getElementById('join-code') as HTMLInputElement).value = code
        ;(document.getElementById('join-name') as HTMLInputElement).value = name

        // Auto-connect
        history.replaceState(null, '', `?room=${code}`)
        const client = new RoomClient(WORKER_URL)
        let activeGame: Game | null = null

        client.on('joined', msg => {
          if (activeGame) { activeGame.loadState(msg.state); return }
          const joinedConfig: GameConfig = {
            ...msg.config,
            boardSize: msg.state.boardSize,
            playerCount: msg.state.players.length as 2 | 3 | 4,
            mode: msg.state.mode,
            eliminationMode: msg.state.eliminationMode,
            lossCondition: msg.state.lossCondition,
          }
          sessionStorage.setItem('chaos-chess-session', JSON.stringify({ roomCode: code, playerIndex: msg.playerIndex }))
          activeGame = startGame(joinedConfig, { client, playerIndex: msg.playerIndex, initialState: msg.state, roomCode: code, initialConnectedCount: msg.connectedCount })
        })
        client.on('state_sync', ({ state: s }) => { activeGame?.loadState(s) })
        client.on('error', ({ message: m }) => {
          if (status) status.textContent = `Error: ${m}`
          removeRecentGame(code)
          renderRecentGames()
        })

        client.connect(code, name, savedIndex)
      }
    }
  }
  renderRecentGames()

  // ── Persist player name ───────────────────────────────────────────────────
  const savedName = localStorage.getItem('chaos-chess-player-name') ?? ''
  const creatorNameEl = document.getElementById('creator-name') as HTMLInputElement
  const joinNameEl    = document.getElementById('join-name')    as HTMLInputElement
  if (savedName) {
    creatorNameEl.value = savedName
    joinNameEl.value    = savedName
  }
  const saveName = (e: Event) => {
    const v = (e.target as HTMLInputElement).value.trim()
    if (v) localStorage.setItem('chaos-chess-player-name', v)
  }
  creatorNameEl.addEventListener('input', saveName)
  joinNameEl.addEventListener('input', saveName)

  // Pre-fill join tab if URL contains ?room=CODE (e.g. shared link or page refresh)
  const prefilledCode = new URLSearchParams(location.search).get('room')?.toUpperCase()
  if (prefilledCode) {
    tabJoin.click()
    ;(document.getElementById('join-code') as HTMLInputElement).value = prefilledCode
    ;(document.getElementById('join-name') as HTMLInputElement).focus()
  }

  // Helper: read config from the local form selectors (shared with online create)
  function readConfigFromForm(): GameConfig {
    const playerCount = parseInt(
      (document.getElementById('player-count') as HTMLSelectElement).value
    ) as 2 | 3 | 4
    const rawElim = (document.getElementById('elimination-mode') as HTMLSelectElement | null)?.value
    const rawLoss = (document.getElementById('loss-condition') as HTMLSelectElement).value
    return {
      boardSize: playerCount === 2 ? 8 : 14,
      playerCount,
      mode: (document.getElementById('game-mode') as HTMLSelectElement).value as GameConfig['mode'],
      eliminationMode: (playerCount >= 3 && rawElim === 'first_blood') ? 'first_blood' : 'last_standing',
      lossCondition: rawLoss === 'all_pieces' ? 'all_pieces' : 'king_captured',
      timerDuration: parseInt((document.getElementById('timer-duration') as HTMLSelectElement).value),
      eventsEnabled: true,
      startLayout: 'side',
      pickupPlacement: (document.getElementById('pickup-placement') as HTMLSelectElement).value as 'random' | 'balanced',
      botSlots: readBotSlots(),
    }
  }

  // ── Create Room ───────────────────────────────────────────────────────────
  document.getElementById('create-room-btn')!.onclick = async () => {
    const btn = document.getElementById('create-room-btn') as HTMLButtonElement
    const status = document.getElementById('online-status')!
    const name = ((document.getElementById('creator-name') as HTMLInputElement).value.trim()) || 'Player 1'
    const config = readConfigFromForm()

    btn.disabled = true
    status.textContent = 'Creating room…'

    try {
      const { roomCode } = await RoomClient.createRoom(WORKER_URL, {
        config,
        playerNames: Array.from({ length: config.playerCount }, (_, i) => {
          if (config.botSlots?.[i]) return `Bot ${i + 1}`
          return i === 0 ? name : `Player ${i + 1}`
        }),
      })

      history.replaceState(null, '', `?room=${roomCode}`)
      status.textContent = `Room ${roomCode} — connecting…`
      const client = new RoomClient(WORKER_URL)
      let activeGame: Game | null = null

      client.on('joined', msg => {
        if (activeGame) { activeGame.loadState(msg.state); return }
        sessionStorage.setItem('chaos-chess-session', JSON.stringify({ roomCode, playerIndex: msg.playerIndex }))
        activeGame = startGame(config, { client, playerIndex: msg.playerIndex, initialState: msg.state, roomCode, initialConnectedCount: msg.connectedCount })
      })
      client.on('state_sync', ({ state: s }) => { activeGame?.loadState(s) })
      client.on('error', ({ message: m }) => { status.textContent = `Error: ${m}`; btn.disabled = false })

      client.connect(roomCode, name, 0)
    } catch (err) {
      status.textContent = `Error: ${err instanceof Error ? err.message : String(err)}`
      btn.disabled = false
    }
  }

  // ── Join Room ─────────────────────────────────────────────────────────────
  document.getElementById('join-room-btn')!.onclick = () => {
    const status = document.getElementById('online-status')!
    const code = ((document.getElementById('join-code') as HTMLInputElement).value.trim()).toUpperCase()
    const name = ((document.getElementById('join-name') as HTMLInputElement).value.trim()) || 'Guest'

    if (!code) { status.textContent = 'Enter a room code'; return }

    // Restore slot if we were previously in this room
    const storedSession = JSON.parse(sessionStorage.getItem('chaos-chess-session') ?? 'null') as { roomCode: string; playerIndex: number } | null
    const savedIndex = storedSession?.roomCode === code ? storedSession.playerIndex : undefined

    history.replaceState(null, '', `?room=${code}`)
    status.textContent = `Joining ${code}…`
    const client = new RoomClient(WORKER_URL)
    let activeGame: Game | null = null

    client.on('joined', msg => {
      if (activeGame) { activeGame.loadState(msg.state); return }
      // Use the server's config so timerDuration and other settings are preserved
      const joinedConfig: GameConfig = {
        ...msg.config,
        boardSize: msg.state.boardSize,
        playerCount: msg.state.players.length as 2 | 3 | 4,
        mode: msg.state.mode,
        eliminationMode: msg.state.eliminationMode,
        lossCondition: msg.state.lossCondition,
      }
      sessionStorage.setItem('chaos-chess-session', JSON.stringify({ roomCode: code, playerIndex: msg.playerIndex }))
      activeGame = startGame(joinedConfig, { client, playerIndex: msg.playerIndex, initialState: msg.state, roomCode: code, initialConnectedCount: msg.connectedCount })
    })
    client.on('state_sync', ({ state: s }) => { activeGame?.loadState(s) })
    client.on('error', ({ message: m }) => { status.textContent = `Error: ${m}` })

    client.connect(code, name, savedIndex)
  }
}

// ── Promotion Dialog ───────────────────────────────────────────────────────────

function showPromotionDialog(state: GameState, move: Move, applyMove: (m: Move) => void) {
  const playerColor = state.players[move.player].color
  const choices: PieceType[] = ['queen', 'rook', 'bishop', 'knight']
  const symbols: Record<PieceType, string> = {
    queen: '♕', rook: '♖', bishop: '♗', knight: '♘', king: '♔', pawn: '♙',
  }

  const overlay = document.createElement('div')
  overlay.style.cssText = `
    position:fixed;inset:0;background:rgba(0,0,0,.6);
    display:flex;align-items:center;justify-content:center;z-index:100;
  `

  const dialog = document.createElement('div')
  dialog.style.cssText = `
    background:#1a1a1a;border:2px solid #555;border-radius:8px;
    padding:16px 24px;text-align:center;
  `
  dialog.innerHTML = `<p style="color:#ccc;margin:0 0 12px">Promote pawn to:</p>`

  for (const type of choices) {
    const btn = document.createElement('button')
    btn.textContent = symbols[type]
    btn.title = type
    btn.style.cssText = `
      font-size:2rem;margin:4px;padding:8px 12px;cursor:pointer;
      background:#333;border:1px solid #666;border-radius:4px;color:${playerColor};
    `
    btn.onclick = () => {
      document.body.removeChild(overlay)
      applyMove({ ...move, promotion: type })
    }
    dialog.appendChild(btn)
  }

  overlay.appendChild(dialog)
  document.body.appendChild(overlay)
}

// ── Game Over Overlay ─────────────────────────────────────────────────────────

const MATERIAL_VALUES: Record<string, number> = {
  pawn: 1, knight: 3, bishop: 3, rook: 5, queen: 9, king: 0,
}

function computeMaterialScores(state: GameState): Record<number, number> {
  const scores: Record<number, number> = {}
  for (const p of state.players) scores[p.index] = 0
  for (const sq of Object.values(state.squares)) {
    if (sq.piece) scores[sq.piece.owner] = (scores[sq.piece.owner] ?? 0) + (MATERIAL_VALUES[sq.piece.type] ?? 0)
  }
  return scores
}

type GameOverOpts = {
  localPlayerIndex?: number      // undefined = local hotseat (no single "local player")
  onRematch?: () => void         // local game or online host — clicking Rematch calls this
  isOnlineNonHost?: boolean      // non-host: Rematch click shows waiting message
  onBackToHome: () => void
}

function showGameOverOverlay(
  winner: import('./core/types').Player | null,
  state: GameState,
  opts: GameOverOpts,
): HTMLDivElement {
  const overlay = document.createElement('div')
  overlay.id = 'game-over-overlay'
  overlay.style.cssText = `
    position:fixed;inset:0;background:rgba(0,0,0,.75);
    display:flex;align-items:center;justify-content:center;z-index:200;
  `

  const box = document.createElement('div')
  box.style.cssText = `
    background:#1a1a1a;border:2px solid #555;border-radius:12px;
    padding:32px 48px;text-align:center;min-width:300px;max-width:420px;
  `

  // Heading — personalised for online players
  const heading = document.createElement('h2')
  heading.style.cssText = 'margin:0 0 8px;font-size:1.8rem;color:#fff;'
  if (opts.localPlayerIndex !== undefined && winner) {
    heading.textContent = winner.index === opts.localPlayerIndex ? 'You Win!' : 'You Were Eliminated'
  } else {
    heading.textContent = 'Game Over'
  }

  // Winner line
  const result = document.createElement('p')
  result.style.cssText = 'margin:0 0 16px;font-size:1.1rem;'
  if (winner) {
    result.innerHTML = `Winner: <strong style="color:${winner.color}">${winner.name}</strong>`
  } else {
    result.innerHTML = `<strong style="color:#aaa">Draw</strong>`
  }

  // Material scores
  const scores = computeMaterialScores(state)
  const scoreRows = state.players.map(p =>
    `<div style="display:flex;justify-content:space-between;padding:2px 0;color:${p.color}">
      <span>${p.name}${p.index === winner?.index ? ' 👑' : ''}</span>
      <span>${scores[p.index] ?? 0} pts</span>
    </div>`
  ).join('')
  const scoreBlock = document.createElement('div')
  scoreBlock.style.cssText = 'margin:0 0 16px;font-size:0.88rem;background:#111;border-radius:6px;padding:8px 12px;text-align:left;'
  scoreBlock.innerHTML = scoreRows

  // Move count
  const moveCount = document.createElement('p')
  moveCount.style.cssText = 'margin:0 0 24px;color:#888;font-size:0.85rem;'
  moveCount.textContent = `${state.moveHistory.length} moves · Turn ${state.turnNumber}`

  // Button row
  const btnRow = document.createElement('div')
  btnRow.style.cssText = 'display:flex;gap:10px;justify-content:center;flex-wrap:wrap;'

  const btnStyle = `font-size:1rem;padding:10px 24px;cursor:pointer;border-radius:6px;color:#fff;`

  // Rematch / Play Again button
  const rematchBtn = document.createElement('button')
  rematchBtn.textContent = 'Rematch'
  rematchBtn.style.cssText = btnStyle + 'background:#1e3a1e;border:1px solid #4caf50;'
  rematchBtn.onmouseover = () => { rematchBtn.style.background = '#2a4e2a' }
  rematchBtn.onmouseout  = () => { rematchBtn.style.background = '#1e3a1e' }
  rematchBtn.onclick = () => {
    if (opts.onRematch) {
      opts.onRematch()
    } else if (opts.isOnlineNonHost) {
      rematchBtn.textContent = 'Waiting for host to start rematch…'
      rematchBtn.disabled = true
      rematchBtn.style.opacity = '0.6'
      rematchBtn.style.cursor = 'default'
    }
  }

  // Back to Home button
  const homeBtn = document.createElement('button')
  homeBtn.textContent = 'Back to Home'
  homeBtn.style.cssText = btnStyle + 'background:#333;border:1px solid #666;'
  homeBtn.onmouseover = () => { homeBtn.style.background = '#444' }
  homeBtn.onmouseout  = () => { homeBtn.style.background = '#333' }
  homeBtn.onclick = () => {
    overlay.remove()
    opts.onBackToHome()
  }

  btnRow.appendChild(rematchBtn)
  btnRow.appendChild(homeBtn)

  box.appendChild(heading)
  box.appendChild(result)
  box.appendChild(scoreBlock)
  box.appendChild(moveCount)
  box.appendChild(btnRow)
  overlay.appendChild(box)
  document.body.appendChild(overlay)
  return overlay
}

// ── Game Start ────────────────────────────────────────────────────────────────

type OnlineOpts = {
  client: RoomClient
  playerIndex: number
  initialState: GameState
  roomCode: string
  initialConnectedCount: number
}

function startGame(config: GameConfig, onlineOpts?: OnlineOpts): Game {
  const setupElement = document.getElementById('setup-screen')
  const gameContainer = document.getElementById('game-container')
  const boardElement = document.getElementById('board')
  const sidebar = document.getElementById('sidebar')

  if (!setupElement || !gameContainer || !boardElement || !sidebar) return new Game(buildInitialState(config))

  setupElement.style.display = 'none'
  gameContainer.style.display = 'block'

  const initialState = onlineOpts?.initialState ?? buildInitialState(config)
  const game = new Game(initialState, { botDelayMs: 800 })

  // Move dispatcher — routes to server (online) or local Game (local)
  const dispatch = onlineOpts ? {
    applyMove:        (m: Move) => onlineOpts.client.sendMove(m),
    submitPendingMove:(m: Move) => onlineOpts.client.sendPendingMove(m),
    resolveRound:     ()       => onlineOpts.client.sendResolveRound(),
  } : {
    applyMove:        (m: Move) => game.applyMove(m),
    submitPendingMove:(m: Move) => game.submitPendingMove(m),
    resolveRound:     ()       => game.resolveRound(),
  }

  // Determine local player perspective:
  // - Online: always the connected player's index
  // - Local with exactly 1 human slot: that human's index (board rotates to their side)
  // - Local hotseat (all humans, or multiple humans): null (no rotation, all pieces selectable)
  let localPlayer: number | null = onlineOpts?.playerIndex ?? null
  if (localPlayer === null && !onlineOpts) {
    const humanIndices = initialState.players
      .map((p, i) => (p.controlledBy === 'human' ? i : -1))
      .filter(i => i >= 0)
    if (humanIndices.length === 1) localPlayer = humanIndices[0]
  }

  // ── Document title tracking ──────────────────────────────────────────────────
  let prevDocumentTitle = document.title

  // ── Reaction state ───────────────────────────────────────────────────────────
  const reactionCooldown: Record<number, number> = {}
  const activeBubbleCount: Record<number, number> = {}
  let openReactionTray: HTMLElement | null = null

  // Inject reaction animation CSS once
  if (!document.getElementById('reaction-styles')) {
    const s = document.createElement('style')
    s.id = 'reaction-styles'
    s.textContent = `@keyframes reaction-pop{0%{transform:scale(0) translateY(6px);opacity:0}60%{transform:scale(1.25) translateY(-2px);opacity:1}100%{transform:scale(1) translateY(0);opacity:1}}`
    document.head.appendChild(s)
  }

  function fireReaction(playerIndex: number, emoji: string) {
    const rowEl = document.getElementById(`player-entry-${playerIndex}`)
    if (!rowEl) return
    const count = activeBubbleCount[playerIndex] ?? 0
    activeBubbleCount[playerIndex] = count + 1
    const rect = rowEl.getBoundingClientRect()
    const bubble = document.createElement('div')
    bubble.textContent = emoji
    bubble.style.cssText = [
      'position:fixed',
      `left:${rect.right + 8}px`,
      `top:${rect.top - count * 38}px`,
      'font-size:1.7rem',
      'z-index:600',
      'pointer-events:none',
      'animation:reaction-pop 0.25s ease-out',
      'transition:opacity 0.4s',
    ].join(';')
    document.body.appendChild(bubble)
    setTimeout(() => { bubble.style.opacity = '0' }, 2100)
    setTimeout(() => {
      bubble.remove()
      activeBubbleCount[playerIndex] = Math.max(0, (activeBubbleCount[playerIndex] ?? 1) - 1)
    }, 2500)
  }

  // Track which player indices have connected (online mode only)
  const connectedPlayerIndices = new Set<number>()
  if (onlineOpts) connectedPlayerIndices.add(onlineOpts.playerIndex)

  // ── Per-player connection status (online mode) ──────────────────────────
  type ConnStatus = 'connected' | 'reconnecting' | 'disconnected'
  const connectionStatus: Record<number, ConnStatus> = {}
  if (onlineOpts) {
    // Pre-populate: we assume only we are connected at start; others unknown
    for (const p of (onlineOpts.initialState?.players ?? [])) {
      connectionStatus[p.index] = p.index === onlineOpts.playerIndex ? 'connected' : 'disconnected'
    }
  }

  function setConnectionStatus(playerIndex: number, status: ConnStatus) {
    connectionStatus[playerIndex] = status
    // Live-update the dot without re-rendering the whole sidebar
    const dot = document.getElementById(`player-dot-${playerIndex}`)
    if (dot) {
      dot.style.background = status === 'connected' ? '#3fb950'
        : status === 'reconnecting' ? '#f0a030'
        : '#6e7681'
      dot.title = status
    }
  }

  // ── Own-connection "Reconnecting…" banner ──────────────────────────────
  let reconnectingBanner: HTMLElement | null = null

  function showReconnectingBanner() {
    if (reconnectingBanner) return
    reconnectingBanner = document.createElement('div')
    reconnectingBanner.textContent = 'Reconnecting…'
    reconnectingBanner.style.cssText = [
      'position:fixed;top:0;left:0;right:0;z-index:500',
      'background:#7a4000;color:#fff;text-align:center',
      'padding:8px 16px;font-size:0.9rem;font-weight:bold',
    ].join(';')
    document.body.prepend(reconnectingBanner)
  }

  function hideReconnectingBanner() {
    reconnectingBanner?.remove()
    reconnectingBanner = null
  }

  // ── Leave Game ──────────────────────────────────────────────────────────────
  let lobbyOverlayEl: HTMLElement | null = null

  // ── Navigation guards ───────────────────────────────────────────────────────
  // Push a sentinel so the back button has somewhere to land, then intercept popstate.
  history.pushState({ chaosChessGame: true }, '')

  const onBeforeUnload = (e: BeforeUnloadEvent) => {
    e.preventDefault()
    // Modern browsers ignore custom messages but require returnValue to be set
    e.returnValue = ''
  }
  window.addEventListener('beforeunload', onBeforeUnload)

  let popstateHandled = false  // prevent re-entry while modal is open
  const onPopState = (e: PopStateEvent) => {
    if (e.state?.chaosChessGame) return  // navigated forward to our sentinel — ignore
    if (popstateHandled) return
    popstateHandled = true
    // Re-push the sentinel so a second back press doesn't slip through
    history.pushState({ chaosChessGame: true }, '')
    confirmLeaveGame(() => { popstateHandled = false }, () => { popstateHandled = false })
  }
  window.addEventListener('popstate', onPopState)

  function removeNavigationGuards() {
    window.removeEventListener('beforeunload', onBeforeUnload)
    window.removeEventListener('popstate', onPopState)
  }

  function leaveGame() {
    removeNavigationGuards()
    clearRoundTimer()
    clearInstantaneousTimer()
    removeLobbyOverlay()
    hideReconnectingBanner()
    document.title = 'Chaos Chess'
    onlineOpts?.client.disconnect()
    coordBar.remove()
    const gc = document.getElementById('game-container')
    const se = document.getElementById('setup-screen')
    if (gc) gc.style.display = 'none'
    if (se) { se.style.display = ''; se.innerHTML = '' }
    history.replaceState(null, '', '/')
    showSetupScreen()
  }

  function confirmLeaveGame(onLeave?: () => void, onStay?: () => void) {
    const isLocal = !onlineOpts
    const msg = isLocal
      ? 'Leave this game? Progress will be lost.'
      : 'Leave this game? You may be able to rejoin using the room code.'
    const overlay = document.createElement('div')
    overlay.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,.7);display:flex;align-items:center;justify-content:center;z-index:300;'
    overlay.innerHTML = `
      <div style="background:#1a1a1a;border:2px solid #555;border-radius:10px;padding:24px 32px;text-align:center;max-width:360px">
        <p style="color:#ddd;margin:0 0 20px;font-size:1rem">${msg}</p>
        <div style="display:flex;gap:10px;justify-content:center">
          <button id="leave-confirm-btn" style="padding:8px 24px;background:#5a1a1a;border:1px solid #a04040;border-radius:6px;color:#fff;cursor:pointer">Leave</button>
          <button id="leave-cancel-btn" style="padding:8px 24px;background:#333;border:1px solid #666;border-radius:6px;color:#fff;cursor:pointer">Stay</button>
        </div>
      </div>`
    document.body.appendChild(overlay)
    overlay.querySelector<HTMLButtonElement>('#leave-confirm-btn')!.onclick = () => { overlay.remove(); onLeave?.(); leaveGame() }
    overlay.querySelector<HTMLButtonElement>('#leave-cancel-btn')!.onclick = () => { overlay.remove(); onStay?.() }
  }

  // ── Lobby Overlay ───────────────────────────────────────────────────────────

  function renderLobbyOverlay(state: GameState) {
    if (!onlineOpts) return
    removeLobbyOverlay()

    const playerRows = state.players.map(p => {
      const isMe = p.index === onlineOpts!.playerIndex
      const isConnected = connectedPlayerIndices.has(p.index)
      const dot = isConnected ? '<span style="color:#4caf50">●</span>' : '<span style="color:#555">○</span>'
      const youLabel = isMe ? ' <span style="color:#888;font-size:0.82rem">(You)</span>' : ''
      const hostLabel = p.index === 0 ? ' <span style="color:#f0a030;font-size:0.78rem">host</span>' : ''
      return `<div style="margin:5px 0;color:${p.color}">${dot} ${p.name}${youLabel}${hostLabel}</div>`
    }).join('')

    const isHost = onlineOpts.playerIndex === 0
    const actionHtml = isHost
      ? `<button id="lobby-start-btn" style="margin-top:18px;padding:10px 32px;font-size:1rem;cursor:pointer;background:#1e3a1e;border:1px solid #4caf50;border-radius:6px;color:#fff">Start Game</button>`
      : `<p style="margin-top:16px;color:#888;font-size:0.9rem">Waiting for host to start…</p>`

    const overlay = document.createElement('div')
    overlay.id = 'lobby-overlay'
    overlay.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,.87);display:flex;align-items:center;justify-content:center;z-index:150;'
    overlay.innerHTML = `
      <div style="background:#1a1a1a;border:2px solid #444;border-radius:12px;padding:28px 36px;min-width:300px;text-align:center">
        <h2 style="margin:0 0 4px;color:#fff;font-size:1.3rem">Room ${onlineOpts.roomCode}</h2>
        <p style="margin:0 0 16px;color:#888;font-size:0.9rem">${connectedCount}/${config.playerCount} players connected</p>
        <div style="text-align:left">${playerRows}</div>
        ${actionHtml}
        <div style="margin-top:16px">
          <button id="lobby-leave-btn" style="padding:5px 14px;font-size:0.82rem;cursor:pointer;background:#333;border:1px solid #555;border-radius:4px;color:#aaa">Leave Room</button>
        </div>
      </div>`
    document.body.appendChild(overlay)
    lobbyOverlayEl = overlay

    overlay.querySelector<HTMLButtonElement>('#lobby-start-btn')?.addEventListener('click', () => {
      onlineOpts!.client.sendStartGame()
    })
    overlay.querySelector<HTMLButtonElement>('#lobby-leave-btn')!.onclick = () => { overlay.remove(); lobbyOverlayEl = null; leaveGame() }
  }

  function removeLobbyOverlay() {
    if (lobbyOverlayEl) { lobbyOverlayEl.remove(); lobbyOverlayEl = null }
  }

  const renderer = new Renderer(boardElement)

  // Coordinate display bar below the board
  const coordBar = document.createElement('div')
  coordBar.className = 'coord-bar'
  boardElement.parentElement!.appendChild(coordBar)

  function updateCoordBar(hoveredKey: string | null) {
    const bs = initialState.boardSize
    const selPart = selectedKey
      ? `<span class="coord-selected">▶ ${formatCoord(selectedKey, bs)}</span>`
      : `<span class="coord-none">no selection</span>`
    const hovPart = hoveredKey
      ? `<span class="coord-hover">⊙ ${formatCoord(hoveredKey, bs)}</span>`
      : `<span class="coord-none">—</span>`
    coordBar.innerHTML = `${selPart}&ensp;|&ensp;${hovPart}`
  }

  function isHoverable(state: GameState, key: string): boolean {
    const sq = state.squares[key]
    if (!sq?.piece) return false
    return state.players[sq.piece.owner]?.alive ?? false
  }

  renderer.setHoverHandler((key) => {
    updateCoordBar(key)
    const state = game.getState()
    if (selectedKey !== null || state.phase === 'gameover') {
      renderer.updateHoverPreview([], state)
      return
    }
    if (key && isHoverable(state, key)) {
      renderer.updateHoverPreview(getLegalMoves(state, key), state)
    } else {
      renderer.updateHoverPreview([], state)
    }
  })

  let selectedKey: string | null = null
  let legalMoves: string[] = []
  let preSelectedKey: string | null = null

  // Board perspective rotation — rotate so the local player's home edge is at the bottom.
  // Only applies when there's a single local player identity (online / bot games).
  // Not applied in hotseat (localPlayer === null).
  function localFacingRotation(): number {
    if (localPlayer === null) return 0
    switch (initialState.players[localPlayer]?.facing) {
      case 'DOWN':  return 180
      case 'RIGHT': return 270
      case 'LEFT':  return 90
      default:      return 0
    }
  }
  renderer.setRotation(localFacingRotation())

  // Helper functions for captured pieces and material advantage
  function getPieceMaterialValue(pieceType: string): number {
    const values: Record<string, number> = {
      pawn: 1,
      knight: 3,
      bishop: 3,
      rook: 5,
      queen: 9,
      king: 0, // King doesn't count toward material
    }
    return values[pieceType] ?? 0
  }

  function calculateMaterialAdvantage(state: GameState): Record<number, number> {
    const playerMaterial: Record<number, number> = {}

    // Initialize all players with 0
    for (const player of state.players) {
      playerMaterial[player.index] = 0
    }

    // Add captured pieces value
    for (const [playerIndex, capturedPieces] of Object.entries(state.capturedBy)) {
      const playerId = parseInt(playerIndex)
      playerMaterial[playerId] = capturedPieces.reduce((total, pieceType) =>
        total + getPieceMaterialValue(pieceType), 0)
    }

    return playerMaterial
  }

  function formatCapturedPieces(capturedPieces: string[], playerOwner: number): string {
    if (!capturedPieces || capturedPieces.length === 0) return ''

    // Group by piece type and count
    const counts: Record<string, number> = {}
    for (const pieceType of capturedPieces) {
      counts[pieceType] = (counts[pieceType] || 0) + 1
    }

    // Format as symbols with counts
    const formatted = Object.entries(counts).map(([pieceType, count]) => {
      const symbol = getPieceSymbol(pieceType, playerOwner)
      return count > 1 ? `${symbol}×${count}` : symbol
    }).join(' ')

    return formatted
  }

  function updateDocumentTitle(state: GameState) {
    let title: string
    if (state.phase === 'gameover') {
      title = 'Game Over — Chaos Chess'
    } else if (state.mode === 'sequential') {
      const activeIsBot = state.players[state.activePlayer]?.controlledBy === 'bot'
      const isMyTurn = !activeIsBot && (localPlayer === null || state.activePlayer === localPlayer)
      title = isMyTurn ? 'Your turn! — Chaos Chess' : 'Waiting… — Chaos Chess'
    } else if (state.mode === 'simultaneous_resolution') {
      const pendingSet = new Set(state.pendingMoves.map(m => m.player))
      const toCheck = localPlayer !== null
        ? [localPlayer]
        : state.players.filter(p => p.alive).map(p => p.index)
      const anyUnsubmitted = toCheck.some(i => !pendingSet.has(i) && state.players[i]?.alive)
      title = anyUnsubmitted ? 'Move now! — Chaos Chess' : 'Waiting… — Chaos Chess'
    } else if (state.mode === 'simultaneous_instantaneous') {
      const pendingSet = new Set(state.pendingMoves.map(m => m.player))
      const toCheck = localPlayer !== null
        ? [localPlayer]
        : state.players.filter(p => p.alive).map(p => p.index)
      const anyUnsubmitted = state.windowOpenAt !== null && toCheck.some(i => !pendingSet.has(i) && state.players[i]?.alive)
      title = anyUnsubmitted ? 'Move now! — Chaos Chess' : 'Waiting… — Chaos Chess'
    } else {
      title = 'Chaos Chess'
    }
    if (title !== prevDocumentTitle) {
      document.title = title
      if ((title === 'Your turn! — Chaos Chess' || title === 'Move now! — Chaos Chess') && document.hidden) {
        sound.check()
      }
      prevDocumentTitle = title
    }
  }

  const turnIndicatorEl = document.getElementById('turn-indicator')
  let prevActivePlayer = -1

  function hexToRgb(hex: string): string {
    const r = parseInt(hex.slice(1, 3), 16)
    const g = parseInt(hex.slice(3, 5), 16)
    const b = parseInt(hex.slice(5, 7), 16)
    return `${r}, ${g}, ${b}`
  }

  function updateTurnIndicator(state: GameState) {
    if (!turnIndicatorEl) return
    if (state.phase === 'gameover' || state.phase === 'lobby') {
      turnIndicatorEl.innerHTML = ''
      prevActivePlayer = -1
      return
    }
    const isResolution   = state.mode === 'simultaneous_resolution'
    const isInstantaneous = state.mode === 'simultaneous_instantaneous'
    if (isResolution || isInstantaneous) {
      turnIndicatorEl.innerHTML = `<span style="color:#aaa">All players move simultaneously</span>`
      prevActivePlayer = -1
      return
    }
    const active = state.players[state.activePlayer]
    if (!active) { turnIndicatorEl.innerHTML = ''; return }

    const playerChanged = state.activePlayer !== prevActivePlayer
    prevActivePlayer = state.activePlayer

    const rgb = hexToRgb(active.color)
    turnIndicatorEl.innerHTML =
      `<div class="turn-pill${playerChanged ? ' turn-pop' : ''}" style="` +
        `background:rgba(${rgb},0.18);` +
        `border-color:${active.color};` +
        `box-shadow:0 0 18px rgba(${rgb},0.45),inset 0 0 10px rgba(${rgb},0.08)` +
      `">` +
        `<span class="turn-pill-dot" style="background:${active.color};box-shadow:0 0 8px ${active.color}"></span>` +
        `<span style="color:${active.color}">${active.name}'s Turn</span>` +
      `</div>`

    if (playerChanged && active.controlledBy === 'human') sound.yourTurn()
  }

  function updateSidebar(state: GameState) {
    if (!sidebar) return
    // Close any open reaction tray since sidebar will be rebuilt
    if (openReactionTray) { openReactionTray.remove(); openReactionTray = null }
    const active = state.players[state.activePlayer]

    let gameOverMsg = ''
    if (state.phase === 'gameover') {
      if (state.winner !== null) {
        gameOverMsg = `<p><strong>Game Over!</strong><br>Winner: <span style="color:${state.players[state.winner].color}">${state.players[state.winner].name}</span></p>`
      } else {
        gameOverMsg = `<p><strong>Game Over — Stalemate!</strong></p>`
      }
    }

    const isResolution = state.mode === 'simultaneous_resolution'
    const isInstantaneous = state.mode === 'simultaneous_instantaneous'
    const pendingSet = new Set(state.pendingMoves.map(m => m.player))


    const materialAdvantage = calculateMaterialAdvantage(state)
    const maxMaterial = Math.max(...Object.values(materialAdvantage))

    const playerRows = state.players.map(p => {
      let statusTag = ''
      if (!p.alive) {
        statusTag = ' <span style="color:#666">(eliminated)</span>'
      } else if (isResolution) {
        statusTag = pendingSet.has(p.index)
          ? ' <span style="color:#4caf50">✓ ready</span>'
          : ' <span style="color:#888">waiting…</span>'
        if (p.inCheck) statusTag += ' <span style="color:#ff4444">⚠ CHECK</span>'
      } else if (isInstantaneous) {
        // New commit window behavior: show locked/unlocked status
        if (state.windowOpenAt !== null) {
          // Window is open - show submission status
          statusTag = pendingSet.has(p.index)
            ? ' <span style="color:#4caf50">✓ locked</span>'
            : ' <span style="color:#888">—</span>'
        } else {
          // Window is closed - ready for next round
          statusTag = ' <span style="color:#4caf50">▶ ready</span>'
        }
        if (p.inCheck) statusTag += ' <span style="color:#ff4444">⚠ CHECK</span>'
      } else {
        if (p.inCheck) statusTag = ' <span style="color:#ff4444">⚠ CHECK</span>'
        if (p.index === state.activePlayer) statusTag += ' ◀'
      }

      // Captured pieces display
      const capturedPieces = state.capturedBy[p.index] || []
      const capturedDisplay = formatCapturedPieces(capturedPieces, p.index)

      // Material advantage indicator
      const playerMaterial = materialAdvantage[p.index] || 0
      const materialIndicator = (playerMaterial > 0 && playerMaterial === maxMaterial && maxMaterial > 0)
        ? ` <span style="color:#4caf50;font-size:0.85rem">+${playerMaterial}</span>`
        : ''

      const capturedSection = capturedDisplay
        ? `<div style="font-size:0.8rem;margin-top:2px;opacity:0.9">${capturedDisplay}${materialIndicator}</div>`
        : materialIndicator
          ? `<div style="font-size:0.8rem;margin-top:2px;opacity:0.9">${materialIndicator}</div>`
          : ''

      const cs = connectionStatus[p.index]
      const dotColor = cs === 'connected' ? '#3fb950' : cs === 'reconnecting' ? '#f0a030' : '#6e7681'
      const dotHtml = onlineOpts
        ? `<span id="player-dot-${p.index}" title="${cs ?? 'unknown'}" style="display:inline-block;width:7px;height:7px;border-radius:50%;background:${dotColor};margin-right:5px;vertical-align:middle;flex-shrink:0"></span>`
        : ''
      const canReact = p.alive && (onlineOpts ? p.index === onlineOpts.playerIndex : true)
      const reactionBtnHtml = canReact
        ? `<button data-reaction-for="${p.index}" style="background:none;border:none;cursor:pointer;font-size:0.85rem;padding:0 3px;opacity:0.55;vertical-align:middle;line-height:1" title="React">💬</button>`
        : ''
      const botBadge = p.controlledBy === 'bot' ? ' <span style="font-size:0.78rem;opacity:0.65">🤖</span>' : ''
      const canConvertToBot = onlineOpts
        && onlineOpts.playerIndex === 0
        && p.index !== 0
        && p.alive
        && p.controlledBy === 'human'
        && (connectionStatus[p.index] === 'disconnected' || connectionStatus[p.index] === 'reconnecting')
      const convertBotBtn = canConvertToBot
        ? `<button data-convert-to-bot="${p.index}" style="font-size:0.72rem;padding:2px 6px;margin-left:4px;background:#2a2a4a;border:1px solid #555;border-radius:4px;color:#9090cc;cursor:pointer" title="Convert to Bot">🤖 Bot</button>`
        : ''
      return `<div id="player-entry-${p.index}" class="player-entry" style="color:${p.color}">${dotHtml}${p.name}${botBadge}${statusTag}${reactionBtnHtml}${convertBotBtn}${capturedSection}</div>`
    }).join('')

    const modeInfo = isResolution
      ? `<p id="round-timer"></p>`
      : isInstantaneous
        ? (state.windowOpenAt !== null
          ? `<div id="instantaneous-timer" style="margin:4px 0"><div style="font-size:0.85rem;color:#aaa;">Commit window</div><div id="timer-bar" style="width:100%;height:8px;background:#333;border-radius:4px;margin-top:2px"><div id="timer-progress" style="height:100%;background:#4caf50;border-radius:4px;transition:width 0.1s;width:100%"></div></div></div>`
          : `<p style="font-size:0.85rem;color:#aaa;margin:4px 0">Instantaneous — move anytime</p>`)
        : `<p>Active: <strong style="color:${active.color}">${active.name}</strong></p>`

    const endRoundVisible = isResolution && state.phase !== 'gameover'
    const undoVisible = !isResolution && !isInstantaneous && state.phase !== 'gameover' && game.canUndo()
    const endRoundBtn = `<button id="end-round-btn" class="sidebar-btn" style="visibility:${endRoundVisible ? 'visible' : 'hidden'}">End Round</button>`
    const undoBtn = `<button id="undo-btn" class="sidebar-btn" style="visibility:${undoVisible ? 'visible' : 'hidden'}">↩ Undo</button>`

    // Move history — last 12 moves, newest first
    const historyItems = [...state.moveHistory].reverse().slice(0, 12).map(m => {
      const player = state.players[m.player]
      const symbol = m.pieceType ? getPieceSymbol(m.pieceType, m.player) : '?'
      const from = formatCoord(m.from, state.boardSize)
      const to   = formatCoord(m.to, state.boardSize)
      const promo = m.promotion ? `=${m.promotion[0].toUpperCase()}` : ''
      return `<div class="move-entry" style="color:${player?.color ?? '#ccc'}">${symbol} ${from}→${to}${promo}</div>`
    }).join('')
    const historySection = state.moveHistory.length > 0
      ? `<div class="move-history"><div class="move-history-label">Moves</div>${historyItems}</div>`
      : ''

    const localP = onlineOpts ? state.players[onlineOpts.playerIndex] : null
    const MODE_LABELS: Record<string, string> = {
      sequential: 'Sequential',
      simultaneous_resolution: 'Simultaneous',
      simultaneous_instantaneous: 'Simultaneous (instant)',
    }
    const roomCodeSection = onlineOpts && localP ? `
      <div class="room-panel">
        <div class="room-label">Room</div>
        <div class="room-code">${onlineOpts.roomCode}</div>
        <div class="room-player" style="color:${localP.color}">You: <strong>${localP.name}</strong></div>
        <div class="room-mode">${MODE_LABELS[state.mode] ?? state.mode}</div>
        ${!allPlayersJoined ? `<div class="room-waiting">Waiting for players… (${connectedCount}/${config.playerCount})</div>` : ''}
        <div class="room-actions">
          <button id="copy-code-btn" class="room-btn">⧉ Code</button>
          <button id="copy-link-btn" class="room-btn">🔗 Link</button>
        </div>
      </div>` : ''

    const leaveGameBtn = `<button id="leave-game-btn" class="sidebar-btn" style="margin-top:8px;width:100%;background:#2a1a1a;border-color:#7a3a3a;color:#cc8888">⬅ Leave Game</button>`

    sidebar.innerHTML = `
      ${roomCodeSection}
      <div id="event-banner" class="event-banner"></div>
      <div class="game-info">
        <h3 style="margin:0 0 4px">Turn ${state.turnNumber}</h3>
        ${modeInfo}
        ${gameOverMsg}
      </div>
      <div class="player-list">${playerRows}</div>
      <div style="display:flex;gap:6px;margin-top:8px;min-height:34px;align-items:center">${endRoundBtn}${undoBtn}</div>
      ${leaveGameBtn}
      ${historySection}
    `

    // Wire copy buttons for online rooms
    const copyCodeBtn = document.getElementById('copy-code-btn')
    if (copyCodeBtn && onlineOpts) {
      copyCodeBtn.onclick = () => navigator.clipboard.writeText(onlineOpts.roomCode)
    }
    const copyLinkBtn = document.getElementById('copy-link-btn')
    if (copyLinkBtn && onlineOpts) {
      copyLinkBtn.onclick = () => navigator.clipboard.writeText(`${location.origin}/?room=${onlineOpts.roomCode}`)
    }

    // Wire leave game button
    const leaveBtn = document.getElementById('leave-game-btn')
    if (leaveBtn) leaveBtn.onclick = () => confirmLeaveGame()

    // Wire end-round button
    const btn = document.getElementById('end-round-btn')
    if (btn) {
      btn.onclick = () => {
        clearRoundTimer()
        dispatch.resolveRound()
      }
    }

    // Wire undo button
    const undoBtnEl = document.getElementById('undo-btn')
    if (undoBtnEl) {
      undoBtnEl.onclick = () => {
        if (game.undoLastMove()) {
          selectedKey = null
          legalMoves = []
          renderer.setSelection(null, [])
        }
      }
    }

    // Wire reaction tray buttons
    for (const p of state.players) {
      const btn = sidebar.querySelector<HTMLButtonElement>(`[data-reaction-for="${p.index}"]`)
      if (!btn) continue
      btn.onclick = (e) => {
        e.stopPropagation()
        if (openReactionTray) { openReactionTray.remove(); openReactionTray = null }
        const rect = btn.getBoundingClientRect()
        const tray = document.createElement('div')
        tray.style.cssText = [
          'position:fixed',
          `top:${rect.bottom + 4}px`,
          `left:${rect.left}px`,
          'background:#1e1e1e',
          'border:1px solid #444',
          'border-radius:8px',
          'padding:5px 7px',
          'display:flex',
          'gap:4px',
          'z-index:700',
        ].join(';')
        const REACTIONS = ['😱', '👏', '🤔', '😤', '🎉']
        for (const emoji of REACTIONS) {
          const eb = document.createElement('button')
          eb.textContent = emoji
          eb.style.cssText = 'background:none;border:none;cursor:pointer;font-size:1.35rem;padding:3px 5px;border-radius:5px;'
          eb.onmouseenter = () => { eb.style.background = '#333' }
          eb.onmouseleave = () => { eb.style.background = 'none' }
          eb.onclick = (ev) => {
            ev.stopPropagation()
            tray.remove()
            openReactionTray = null
            const now = Date.now()
            if ((reactionCooldown[p.index] ?? 0) + 3000 > now) return
            reactionCooldown[p.index] = now
            if (onlineOpts) {
              onlineOpts.client.sendReaction(emoji)
            } else {
              fireReaction(p.index, emoji)
            }
          }
          tray.appendChild(eb)
        }
        document.body.appendChild(tray)
        openReactionTray = tray
        setTimeout(() => {
          const closeOnOutside = (ev: MouseEvent) => {
            if (!tray.contains(ev.target as Node)) {
              tray.remove()
              openReactionTray = null
              document.removeEventListener('click', closeOnOutside, true)
            }
          }
          document.addEventListener('click', closeOnOutside, true)
        }, 0)
      }
    }

    // Wire "Convert to Bot" buttons (online, host only)
    if (onlineOpts) {
      for (const p of state.players) {
        const convertBtn = sidebar.querySelector<HTMLButtonElement>(`[data-convert-to-bot="${p.index}"]`)
        if (!convertBtn) continue
        convertBtn.onclick = () => {
          convertBtn.disabled = true
          convertBtn.textContent = 'Converting…'
          onlineOpts.client.sendConvertToBot(p.index)
        }
      }
    }

    // Update document title
    updateDocumentTitle(state)

    // Refresh timer displays (text was just wiped by innerHTML)
    updateRoundTimerDisplay()
    updateInstantaneousTimerDisplay()
  }

  // ── Simultaneous-mode state ──────────────────────────────────────────────────
  // Track which player the current user is controlling in simultaneous_resolution mode.
  // In hot-seat play, any player can click — we determine owner by which piece is clicked.
  let simultaneousTimer: ReturnType<typeof setInterval> | null = null
  let roundTimeLeft = 0

  // Instantaneous mode timer
  let instantaneousTimer: ReturnType<typeof setInterval> | null = null
  let windowTimeLeft = 0

  // Track connected player count for online rooms — timer only starts when all have joined.
  let allPlayersJoined = !onlineOpts  // true immediately for local games
  let connectedCount = onlineOpts?.initialConnectedCount ?? config.playerCount

  function maybeStartTimer() {
    const state = game.getState()
    if (!allPlayersJoined || state.phase === 'lobby') return
    if (config.mode === 'simultaneous_resolution' && config.timerDuration > 0) {
      startRoundTimer(config.timerDuration)
    }
  }

  if (onlineOpts) {
    const checkAllJoined = () => {
      if (!allPlayersJoined && connectedCount >= config.playerCount) {
        allPlayersJoined = true
        if (game.getState().phase !== 'lobby') maybeStartTimer()
        updateSidebar(game.getState())
      }
    }
    onlineOpts.client.on('player_joined', (msg) => {
      connectedPlayerIndices.add(msg.playerIndex)
      setConnectionStatus(msg.playerIndex, 'connected')
      connectedCount++
      checkAllJoined()
      if (game.getState().phase === 'lobby') renderLobbyOverlay(game.getState())
    })

    onlineOpts.client.on('player_disconnected', (msg) => {
      setConnectionStatus(msg.playerIndex, 'reconnecting')
    })

    onlineOpts.client.on('player_reconnected', (msg) => {
      setConnectionStatus(msg.playerIndex, 'connected')
    })

    onlineOpts.client.on('player_left', (msg) => {
      setConnectionStatus(msg.playerIndex, 'disconnected')
      connectedCount = Math.max(0, connectedCount - 1)
      if (msg.timedOut) {
        const name = game.getState().players[msg.playerIndex]?.name ?? `Player ${msg.playerIndex + 1}`
        const toast = document.createElement('div')
        toast.textContent = `${name} disconnected — their turns will be skipped`
        toast.style.cssText = [
          'position:fixed;bottom:24px;left:50%;transform:translateX(-50%)',
          'background:#333;color:#ccc;padding:8px 18px;border-radius:6px',
          'font-size:0.88rem;z-index:400;pointer-events:none',
          'border:1px solid #555',
        ].join(';')
        document.body.appendChild(toast)
        setTimeout(() => toast.remove(), 5000)
      }
    })

    onlineOpts.client.on('connectionChange', (msg) => {
      if (msg.status === 'reconnecting') {
        showReconnectingBanner()
      } else if (msg.status === 'connected') {
        hideReconnectingBanner()
        setConnectionStatus(onlineOpts.playerIndex, 'connected')
      } else {
        hideReconnectingBanner()
      }
    })

    onlineOpts.client.on('reaction', (msg) => { fireReaction(msg.playerIndex, msg.emoji) })

    checkAllJoined()  // Handle case where room is already full on join
  }

  function startRoundTimer(seconds: number) {
    clearRoundTimer()
    roundTimeLeft = seconds
    updateRoundTimerDisplay()
    simultaneousTimer = setInterval(() => {
      roundTimeLeft--
      updateRoundTimerDisplay()
      if (roundTimeLeft <= 0) {
        clearRoundTimer()
        dispatch.resolveRound()
      }
    }, 1000)
  }

  function clearRoundTimer() {
    if (simultaneousTimer !== null) {
      clearInterval(simultaneousTimer)
      simultaneousTimer = null
    }
  }

  function updateRoundTimerDisplay() {
    const el = document.getElementById('round-timer')
    if (!el) return
    if (config.timerDuration > 0) {
      el.textContent = `Round ends in: ${roundTimeLeft}s`
      el.style.color = roundTimeLeft <= 5 ? '#ff4444' : '#ccc'
    } else {
      el.textContent = ''
    }
  }

  function startInstantaneousTimer(state: GameState) {
    if (state.mode !== 'simultaneous_instantaneous' || state.windowOpenAt === null) {
      return
    }

    clearInstantaneousTimer()
    const elapsed = Date.now() - state.windowOpenAt
    windowTimeLeft = Math.max(0, Math.ceil((state.instantaneousWindowMs - elapsed) / 1000))

    updateInstantaneousTimerDisplay()
    instantaneousTimer = setInterval(() => {
      windowTimeLeft--
      updateInstantaneousTimerDisplay()
      if (windowTimeLeft <= 0) {
        clearInstantaneousTimer()
      }
    }, 1000)
  }

  function clearInstantaneousTimer() {
    if (instantaneousTimer !== null) {
      clearInterval(instantaneousTimer)
      instantaneousTimer = null
    }
  }

  function updateInstantaneousTimerDisplay() {
    const progressEl = document.getElementById('timer-progress')
    if (!progressEl) return

    const state = game.getState()
    if (state.mode !== 'simultaneous_instantaneous' || state.windowOpenAt === null) {
      clearInstantaneousTimer()
      return
    }

    // Calculate progress percentage
    const elapsed = Date.now() - state.windowOpenAt
    const progress = Math.max(0, Math.min(100, 100 - (elapsed / state.instantaneousWindowMs) * 100))

    progressEl.style.width = `${progress}%`
    progressEl.style.backgroundColor = progress > 20 ? '#4caf50' : '#ff4444'
  }

  function handleSquareClick(key: string) {
    const state = game.getState()
    if (state.phase === 'gameover') return

    if (state.mode === 'simultaneous_resolution') {
      handleSimultaneousClick(key, state)
    } else if (state.mode === 'simultaneous_instantaneous') {
      handleInstantaneousClick(key, state)
    } else {
      handleSequentialClick(key, state)
    }
  }

  // In simultaneous_resolution mode, clicking selects any own-piece (any alive player).
  // Submitting a move buffers it; a player may change their pending move until round resolves.
  function handleSimultaneousClick(key: string, state: GameState) {
    const sq = state.squares[key]

    if (selectedKey === null) {
      // Select any alive human player's own piece (bots excluded)
      if (sq?.piece) {
        const owner = sq.piece.owner
        if (state.players[owner]?.alive &&
            state.players[owner]?.controlledBy !== 'bot' &&
            (localPlayer === null || owner === localPlayer)) {
          selectedKey = key
          legalMoves = getLegalMoves(state, key)
          renderer.setSelection(selectedKey, legalMoves)
          renderer.render(state)
          updateCoordBar(key)
        }
      }
    } else {
      const selectedSq = state.squares[selectedKey]
      const owner = selectedSq?.piece?.owner ?? -1

      if (key === selectedKey) {
        selectedKey = null; legalMoves = []
        renderer.setSelection(null, [])
        renderer.render(state)
        updateCoordBar(key)
        return
      }
      if (legalMoves.includes(key)) {
        const move: Move = { player: owner, from: selectedKey, to: key }
        selectedKey = null
        legalMoves = []
        renderer.setSelection(null, [])
        updateCoordBar(key)
        if (isPawnPromotion(state, move)) {
          showPromotionDialog(state, move, dispatch.applyMove)
        } else {
          dispatch.submitPendingMove(move)
        }
      } else if (sq?.piece && state.players[sq.piece.owner]?.alive &&
                 state.players[sq.piece.owner]?.controlledBy !== 'bot' &&
                 (localPlayer === null || sq.piece.owner === localPlayer)) {
        // Re-select a different human's own piece
        selectedKey = key
        legalMoves = getLegalMoves(state, key)
        renderer.setSelection(selectedKey, legalMoves)
        renderer.render(state)
        updateCoordBar(key)
      } else {
        // Deselect
        selectedKey = null
        legalMoves = []
        renderer.setSelection(null, [])
        renderer.render(state)
        updateCoordBar(null)
      }
    }
  }

  // In instantaneous mode, players can select and move during the commit window,
  // but only if they haven't already submitted a move for this window.
  function handleInstantaneousClick(key: string, state: GameState) {
    const sq = state.squares[key]
    const pendingSet = new Set(state.pendingMoves.map(m => m.player))

    if (selectedKey === null) {
      if (sq?.piece) {
        const owner = sq.piece.owner
        // Allow selection only if:
        // 1. Player is alive and human (bots are excluded)
        // 2. Player hasn't already submitted a move this window (when window is open)
        // 3. It's the local player or in hot-seat mode
        const hasSubmitted = state.windowOpenAt !== null && pendingSet.has(owner)
        if (state.players[owner]?.alive &&
            state.players[owner]?.controlledBy !== 'bot' &&
            !hasSubmitted && (localPlayer === null || owner === localPlayer)) {
          selectedKey = key
          legalMoves = getLegalMoves(state, key)
          renderer.setSelection(selectedKey, legalMoves)
          renderer.render(state)
          updateCoordBar(key)
        }
      }
    } else {
      const selectedSq = state.squares[selectedKey]
      const owner = selectedSq?.piece?.owner ?? -1

      if (key === selectedKey) {
        selectedKey = null; legalMoves = []
        renderer.setSelection(null, [])
        renderer.render(state)
        updateCoordBar(key)
        return
      }
      if (legalMoves.includes(key)) {
        const move: Move = { player: owner, from: selectedKey, to: key }
        selectedKey = null
        legalMoves = []
        renderer.setSelection(null, [])
        updateCoordBar(key)
        if (isPawnPromotion(state, move)) {
          showPromotionDialog(state, move, dispatch.applyMove)
        } else {
          dispatch.applyMove(move)
        }
      } else if (sq?.piece && state.players[sq.piece.owner]?.alive &&
                 state.players[sq.piece.owner]?.controlledBy !== 'bot') {
        // Re-select a different human piece (if allowed)
        const hasSubmitted = state.windowOpenAt !== null && pendingSet.has(sq.piece.owner)
        if (!hasSubmitted && (localPlayer === null || sq.piece.owner === localPlayer)) {
          selectedKey = key
          legalMoves = getLegalMoves(state, key)
          renderer.setSelection(selectedKey, legalMoves)
          renderer.render(state)
          updateCoordBar(key)
        }
      } else {
        // Deselect
        selectedKey = null
        legalMoves = []
        renderer.setSelection(null, [])
        renderer.render(state)
        updateCoordBar(null)
      }
    }
  }

  function handleSequentialClick(key: string, state: GameState) {
    const sq = state.squares[key]

    // Is this an "own" piece?
    // - Online (localPlayer set): must be localPlayer's piece.
    // - Hotseat (localPlayer null): any alive human player's piece qualifies (bots excluded).
    const isOwnPiece = sq?.piece != null &&
      state.players[sq.piece.owner]?.alive === true &&
      state.players[sq.piece.owner]?.controlledBy !== 'bot' &&
      (localPlayer === null || sq.piece.owner === localPlayer)

    // Is it this piece's turn right now?
    const pieceOwner = sq?.piece?.owner ?? -1
    const isThisPiecesTurn = pieceOwner === state.activePlayer

    if (selectedKey === null && preSelectedKey === null) {
      if (isOwnPiece && isThisPiecesTurn) {
        // Active select — show legal moves immediately
        selectedKey = key
        legalMoves = getLegalMoves(state, key)
        renderer.setSelection(selectedKey, legalMoves)
        renderer.render(state)
        updateCoordBar(key)
      } else if (isOwnPiece) {
        // Pre-select — queue this piece for when its turn arrives
        preSelectedKey = key
        renderer.setPreSelection(preSelectedKey)
        renderer.render(state)
        updateCoordBar(key)
      }
      // Enemy piece or empty square: no response
    } else if (selectedKey !== null) {
      // Active selection state
      if (key === selectedKey) {
        selectedKey = null; legalMoves = []
        renderer.setSelection(null, [])
        renderer.render(state)
        updateCoordBar(key)
        return
      }
      if (legalMoves.includes(key)) {
        const move: Move = { player: state.activePlayer, from: selectedKey, to: key }
        selectedKey = null; legalMoves = []
        renderer.setSelection(null, [])
        updateCoordBar(key)
        if (isPawnPromotion(state, move)) {
          showPromotionDialog(state, move, dispatch.applyMove)
        } else {
          dispatch.applyMove(move)
        }
      } else if (isOwnPiece && isThisPiecesTurn) {
        // Re-select a different active piece
        selectedKey = key
        legalMoves = getLegalMoves(state, key)
        renderer.setSelection(selectedKey, legalMoves)
        renderer.render(state)
        updateCoordBar(key)
      } else if (isOwnPiece) {
        // Clicked own piece that can't move yet — switch to pre-selection
        selectedKey = null; legalMoves = []
        renderer.setSelection(null, [])
        preSelectedKey = key
        renderer.setPreSelection(preSelectedKey)
        renderer.render(state)
        updateCoordBar(key)
      } else {
        // Empty square or enemy piece — deselect
        selectedKey = null; legalMoves = []
        renderer.setSelection(null, [])
        renderer.render(state)
        updateCoordBar(null)
      }
    } else {
      // Pre-selection state (preSelectedKey !== null)
      if (key === preSelectedKey) {
        preSelectedKey = null
        renderer.setPreSelection(null)
        renderer.render(state)
        updateCoordBar(key)
        return
      }
      if (isOwnPiece && isThisPiecesTurn) {
        // This piece is now active — switch from pre-select to active select
        preSelectedKey = null
        renderer.setPreSelection(null)
        selectedKey = key
        legalMoves = getLegalMoves(state, key)
        renderer.setSelection(selectedKey, legalMoves)
        renderer.render(state)
        updateCoordBar(key)
      } else if (isOwnPiece) {
        // Update pre-selection to a different own piece
        preSelectedKey = key
        renderer.setPreSelection(preSelectedKey)
        renderer.render(state)
        updateCoordBar(key)
      } else {
        // Empty square or enemy piece — clear pre-selection
        preSelectedKey = null
        renderer.setPreSelection(null)
        renderer.render(state)
        updateCoordBar(null)
      }
    }
  }

  renderer.setClickHandler(handleSquareClick)

  // ── Keyboard navigation ──────────────────────────────────────────────────────
  let keyboardCursor: string | null = null

  function moveCursor(dc: number, dr: number) {
    const state = game.getState()
    const [col, row] = (keyboardCursor ?? '0,0').split(',').map(Number)
    let nc = col + dc
    let nr = row + dr
    // Clamp within board
    nc = Math.max(0, Math.min(state.boardSize - 1, nc))
    nr = Math.max(0, Math.min(state.boardSize - 1, nr))
    // Skip invalid squares
    let attempts = state.boardSize * 2
    while (!state.squares[`${nc},${nr}`] && attempts-- > 0) {
      nc = Math.max(0, Math.min(state.boardSize - 1, nc + dc))
      nr = Math.max(0, Math.min(state.boardSize - 1, nr + dr))
    }
    const newKey = `${nc},${nr}`
    if (state.squares[newKey]) {
      keyboardCursor = newKey
      renderer.setCursor(keyboardCursor)
      renderer.render(state)
      updateCoordBar(keyboardCursor)
      // Move DOM focus to the square element
      const el = boardElement!.querySelector(`[data-key="${newKey}"]`) as HTMLElement | null
      el?.focus({ preventScroll: false })
    }
  }

  boardElement.addEventListener('keydown', (e) => {
    const state = game.getState()
    if (state.phase === 'gameover') return

    // If a square inside the board is focused, update keyboardCursor
    const focused = document.activeElement as HTMLElement
    if (focused?.dataset?.key) keyboardCursor = focused.dataset.key

    switch (e.key) {
      case 'ArrowUp':    e.preventDefault(); moveCursor(0, -1);  break
      case 'ArrowDown':  e.preventDefault(); moveCursor(0,  1);  break
      case 'ArrowLeft':  e.preventDefault(); moveCursor(-1, 0);  break
      case 'ArrowRight': e.preventDefault(); moveCursor( 1, 0);  break
      case 'Enter':
      case ' ':
        e.preventDefault()
        if (keyboardCursor) handleSquareClick(keyboardCursor)
        break
      case 'Escape':
        selectedKey = null; legalMoves = []
        preSelectedKey = null
        renderer.setSelection(null, [])
        renderer.setPreSelection(null)
        renderer.render(state)
        updateCoordBar(keyboardCursor)
        break
    }
  })

  game.on('stateChange', (prev, next) => {
    // Lobby → active: dismiss overlay, start timer
    if (prev.phase === 'lobby' && next.phase !== 'lobby') {
      removeLobbyOverlay()
      maybeStartTimer()
    }
    // Stay in lobby: refresh the overlay to reflect new player names / counts
    if (next.phase === 'lobby') {
      renderLobbyOverlay(next)
      updateSidebar(next)
      return
    }

    // Sound: detect capture vs plain move by comparing piece count
    const prevCount = Object.values(prev.squares).filter(s => s.piece).length
    const nextCount = Object.values(next.squares).filter(s => s.piece).length
    if (nextCount < prevCount) {
      sound.capture()
    } else if (next.moveHistory.length > prev.moveHistory.length || next.turnNumber !== prev.turnNumber) {
      sound.move()
    }

    // Pre-selection management: promote when it's this piece's turn, clear if captured
    if (preSelectedKey !== null) {
      const prevOwner = prev.squares[preSelectedKey]?.piece?.owner
      const nextSq = next.squares[preSelectedKey]
      if (nextSq?.piece == null || nextSq.piece.owner !== prevOwner) {
        // Piece was captured or replaced — clear
        preSelectedKey = null
        renderer.setPreSelection(null)
        renderer.setSelection(null, [])
        selectedKey = null; legalMoves = []
      } else if (nextSq.piece.owner === next.activePlayer) {
        // Pre-selected piece's player is now active — promote to active selection
        selectedKey = preSelectedKey
        legalMoves = getLegalMoves(next, selectedKey)
        preSelectedKey = null
        renderer.setPreSelection(null)
        renderer.setSelection(selectedKey, legalMoves)
      }
      // else: not this player's turn yet — keep pre-selection as-is
    } else {
      renderer.setSelection(null, [])
      selectedKey = null
      legalMoves = []
    }

    // Snapshot source rect before DOM rebuild for slide animation
    let pieceAnim: { fromRect: DOMRect; toKey: string; symbol: string; color: string } | null = null
    if (next.moveHistory.length > prev.moveHistory.length && boardElement) {
      const move = next.moveHistory[next.moveHistory.length - 1]
      const movingPiece = prev.squares[move.from]?.piece
      if (movingPiece) {
        const fromEl = boardElement.querySelector<HTMLElement>(`[data-key="${move.from}"]`)
        const fromRect = fromEl?.getBoundingClientRect()
        if (fromRect) {
          pieceAnim = {
            fromRect,
            toKey: move.to,
            symbol: getPieceSymbol(movingPiece.type, movingPiece.owner),
            color: prev.players[movingPiece.owner]?.color ?? '#fff',
          }
        }
      }
    }

    // Detect pickup consumption: present in prev at move destination, gone in next
    let pickedUp: { icon: string; name: string; color: string } | null = null
    if (next.moveHistory.length > prev.moveHistory.length) {
      const move = next.moveHistory[next.moveHistory.length - 1]
      const consumed = prev.pickups[move.to]
      if (consumed && !next.pickups[move.to] && move.pieceType !== 'king') {
        if (consumed.kind === 'upgrade') {
          pickedUp = { icon: '⬆', name: 'Upgrade!', color: '#fbbf24' }
        } else {
          const TRANSFORM_ICONS: Record<string, string> = { queen: '♛', rook: '♜', bishop: '♝', knight: '♞', pawn: '♟' }
          const TRANSFORM_COLORS: Record<string, string> = { queen: '#c084fc', rook: '#60a5fa', bishop: '#34d399', knight: '#f87171', pawn: '#94a3b8' }
          const label = consumed.to.charAt(0).toUpperCase() + consumed.to.slice(1)
          pickedUp = {
            icon: TRANSFORM_ICONS[consumed.to] ?? '♟',
            name: `Transform → ${label}!`,
            color: TRANSFORM_COLORS[consumed.to] ?? '#fff',
          }
        }
      }
    }

    renderer.render(next)

    if (pieceAnim && boardElement) {
      animatePieceMove(boardElement, pieceAnim.fromRect, pieceAnim.toKey, pieceAnim.symbol, pieceAnim.color, renderer.getRotation())
    }

    // Show pickup overlay after slide animation lands (~230ms delay)
    if (pickedUp) {
      const { icon, name, color } = pickedUp
      setTimeout(() => showBoardOverlay(icon, name, color), 230)
    }

    updateTurnIndicator(next)
    updateSidebar(next)
    updateEventBanner(next, renderer, localFacingRotation())
    updateCoordBar(null)

    // In simultaneous_resolution mode, restart the round timer after a round resolves.
    // A round just resolved when pendingMoves went from non-empty to empty.
    const roundJustResolved = prev.pendingMoves.length > 0 && next.pendingMoves.length === 0
    if (next.mode === 'simultaneous_resolution' && next.phase !== 'gameover' && roundJustResolved) {
      maybeStartTimer()
    }

    // In simultaneous_instantaneous mode, manage the commit window timer.
    // Window opens when windowOpenAt goes from null to a timestamp.
    // Window closes when windowOpenAt goes from timestamp to null.
    if (next.mode === 'simultaneous_instantaneous' && next.phase !== 'gameover') {
      const windowJustOpened = prev.windowOpenAt === null && next.windowOpenAt !== null
      const windowJustClosed = prev.windowOpenAt !== null && next.windowOpenAt === null

      if (windowJustOpened) {
        startInstantaneousTimer(next)
      } else if (windowJustClosed) {
        clearInstantaneousTimer()
      }
    }
  })

  game.on('checkAlert', (player) => {
    sound.check()
    const banner = document.createElement('div')
    banner.textContent = `${player.name} is in CHECK!`
    banner.style.cssText = `
      position:fixed;top:16px;left:50%;transform:translateX(-50%);
      background:#8b0000;color:#fff;padding:8px 20px;border-radius:6px;
      font-weight:bold;z-index:50;pointer-events:none;
    `
    document.body.appendChild(banner)
    setTimeout(() => banner.remove(), 2000)
  })

  const CHAOS_EVENT_NAMES: Record<string, string> = {
    outer_ring_cw:  'Chaos Spin!',
    outer_ring_ccw: 'Chaos Spin!',
    center_180:     'Chaos Flip!',
    diagonal_ne:    'Chaos Shift!',
    diagonal_sw:    'Chaos Shift!',
    diagonal_nw:    'Chaos Shift!',
    diagonal_se:    'Chaos Shift!',
  }

  function showBoardEventUI(event: BoardEvent, newState: GameState) {
    // Flash the board to signal the transform happened
    const boardEl = document.getElementById('board')
    if (boardEl) {
      boardEl.classList.add('board-event-flash')
      setTimeout(() => boardEl.classList.remove('board-event-flash'), 600)
    }

    const color = EVENT_COLOR[event.type] ?? '#ffa500'
    const rotation = localFacingRotation()
    const icon  = orientedEventIcon(event.type, rotation)
    const name  = CHAOS_EVENT_NAMES[event.type] ?? 'Chaos Event!'

    showBoardOverlay(icon, name, color)

    // Small toast (kept for accessibility / visibility outside board area)
    const label = orientedEventLabel(event.type, rotation)
    const toast = document.createElement('div')
    toast.textContent = `\u{1F300} ${label}!`
    toast.style.cssText = `
      position:fixed;top:16px;left:50%;transform:translateX(-50%);
      background:#111;color:${color};padding:8px 22px;border-radius:6px;
      font-weight:bold;font-size:1.05rem;z-index:50;pointer-events:none;
      border:1px solid ${color};
    `
    document.body.appendChild(toast)
    setTimeout(() => toast.remove(), 2500)

    // Re-render with updated state (transform already applied)
    renderer.render(newState)
    updateTurnIndicator(newState)
    updateSidebar(newState)
    updateEventBanner(newState, renderer, localFacingRotation())
  }

  // Local mode: fired by Game.ts after transform applied
  game.on('boardEventFired', showBoardEventUI)

  // Online mode: server sends board_event_fired before state_sync
  onlineOpts?.client.on('board_event_fired', ({ event, state: s }) => {
    showBoardEventUI(event, s)
  })

  game.on('gameOver', (winner, finalState) => {
    removeNavigationGuards()
    if (onlineOpts?.roomCode) markRecentGameCompleted(onlineOpts.roomCode)
    clearRoundTimer()
    selectedKey = null; legalMoves = []
    preSelectedKey = null
    renderer.setSelection(null, [])
    renderer.setPreSelection(null)
    renderer.render(finalState)
    updateTurnIndicator(finalState)
    updateSidebar(finalState)

    const onBackToHome = () => {
      leaveGame()
    }

    let onRematch: (() => void) | undefined
    let isOnlineNonHost = false

    if (!onlineOpts) {
      // Local hotseat — Rematch resets the game immediately
      onRematch = () => {
        coordBar.remove()
        const gc = document.getElementById('game-container')
        const se = document.getElementById('setup-screen')
        if (gc) gc.style.display = 'none'
        if (se) { se.style.display = ''; se.innerHTML = '' }
        history.replaceState(null, '', '/')
        startGame(config)
      }
    } else if (onlineOpts.playerIndex === 0) {
      // Online host — Rematch sends REMATCH_REQUEST
      onRematch = () => onlineOpts!.client.sendRematchRequest()
    } else {
      // Online non-host — button shows waiting message on click
      isOnlineNonHost = true
    }

    const overlay = showGameOverOverlay(winner, finalState, {
      localPlayerIndex: onlineOpts?.playerIndex,
      onRematch,
      isOnlineNonHost,
      onBackToHome,
    })

    // Online: dismiss the game-over overlay when server signals rematch is ready
    if (onlineOpts) {
      onlineOpts.client.on('rematch_ready', () => { overlay.remove() })
    }
  })

  // Save recent game entry
  const recentPlayerName = onlineOpts
    ? (initialState.players[onlineOpts.playerIndex]?.name ?? 'Player 1')
    : 'Local'
  saveRecentGame({
    roomCode: onlineOpts?.roomCode ?? null,
    playerIndex: onlineOpts?.playerIndex ?? 0,
    playerName: recentPlayerName,
    mode: config.mode,
    playerCount: config.playerCount,
    timestamp: Date.now(),
  })

  // Initial render
  renderer.render(initialState)
  updateTurnIndicator(initialState)
  updateSidebar(initialState)
  updateEventBanner(initialState, renderer, localFacingRotation())
  updateCoordBar(null)

  // Show lobby overlay for online rooms that start in lobby phase
  if (initialState.phase === 'lobby' && onlineOpts) {
    renderLobbyOverlay(initialState)
  }

  // For local games, start timer immediately. For online games, maybeStartTimer()
  // is called by checkAllJoined() once all players have connected.
  if (!onlineOpts) maybeStartTimer()

  return game
}

// ── Bootstrap ─────────────────────────────────────────────────────────────────

showSetupScreen()
